import phoneFormatter from "phone-formatter";
import { store } from "./store";
import { toastr } from "react-redux-toastr";
import { SET_LOCKED, UPDATE_DEVICE_ID, UPDATE_ONLINE_STATUS, WORKER_ACTIVE } from "./store/actions/constants";
import { createPushSubscription, getUser, updatePushSubscription } from "./store/actions/user";
import { deviceDetect, deviceType } from "react-device-detect";
import Resizer from "react-image-file-resizer";
import LogRocket from "logrocket";
import { v4 } from "uuid";
import ReactGA from "react-ga4";
import { getAuthChallenge, storeCredential, verifyCredential } from "./store/actions/authentication";
import { client } from "@passwordless-id/webauthn";
import moment from "moment";
import { generateSlug } from "random-word-slugs";
import config from "./config";
import debug from "./utilities/debug";
import midTruncate, { TruncatedId } from "./utilities/truncate";

export const SMART_SCHEDULE_THRESHOLD = 7;
export const SMART_SCHEDULE_LOOKBACK = 30;
export const MAX_FILE_SIZE = 26214400;
export const MAX_FILES = 25;
export const MAX_PAGES = 25;
export const DATA_THRESHOLD = 15;
export const MAX_PROMPT_LENGTH = 1000;
export const languages = [
  { value: 'af', label: 'Afrikaans', countryCode: 'za' }, // South Africa
  { value: 'sq', label: 'Albanian', countryCode: 'al' }, // Albania
  { value: 'am', label: 'Amharic', countryCode: 'et' }, // Ethiopia
  { value: 'ar', label: 'Arabic', countryCode: 'sa' }, // Saudi Arabia
  { value: 'hy', label: 'Armenian', countryCode: 'am' }, // Armenia
  { value: 'as', label: 'Assamese', countryCode: 'in' }, // India
  { value: 'ay', label: 'Aymara', countryCode: 'bo' }, // Bolivia
  { value: 'az', label: 'Azerbaijani', countryCode: 'az' }, // Azerbaijan
  { value: 'bm', label: 'Bambara', countryCode: 'ml' }, // Mali
  { value: 'eu', label: 'Basque', countryCode: 'es' }, // Spain
  { value: 'be', label: 'Belarusian', countryCode: 'by' }, // Belarus
  { value: 'bn', label: 'Bengali', countryCode: 'bd' }, // Bangladesh
  { value: 'bho', label: 'Bhojpuri', countryCode: 'in' }, // India
  { value: 'bs', label: 'Bosnian', countryCode: 'ba' }, // Bosnia and Herzegovina
  { value: 'bg', label: 'Bulgarian', countryCode: 'bg' }, // Bulgaria
  { value: 'ca', label: 'Catalan', countryCode: 'es' }, // Spain
  { value: 'ceb', label: 'Cebuano', countryCode: 'ph' }, // Philippines
  { value: 'zh-CN', label: 'Chinese (Simplified)', countryCode: 'cn' }, // China
  { value: 'zh-TW', label: 'Chinese (Traditional)', countryCode: 'tw' }, // Taiwan
  { value: 'co', label: 'Corsican', countryCode: 'fr' }, // France
  { value: 'hr', label: 'Croatian', countryCode: 'hr' }, // Croatia
  { value: 'cs', label: 'Czech', countryCode: 'cz' }, // Czech Republic
  { value: 'da', label: 'Danish', countryCode: 'dk' }, // Denmark
  { value: 'dv', label: 'Dhivehi', countryCode: 'mv' }, // Maldives
  { value: 'doi', label: 'Dogri', countryCode: 'in' }, // India
  { value: 'nl', label: 'Dutch', countryCode: 'nl' }, // Netherlands
  { value: 'en', label: 'English', countryCode: 'gb' }, // United Kingdom
  { value: 'eo', label: 'Esperanto', countryCode: 'xx' }, // Not applicable
  { value: 'et', label: 'Estonian', countryCode: 'ee' }, // Estonia
  { value: 'ee', label: 'Ewe', countryCode: 'gh' }, // Ghana
  { value: 'fil', label: 'Filipino (Tagalog)', countryCode: 'ph' }, // Philippines
  { value: 'fi', label: 'Finnish', countryCode: 'fi' }, // Finland
  { value: 'fr', label: 'French', countryCode: 'fr' }, // France
  { value: 'fy', label: 'Frisian', countryCode: 'nl' }, // Netherlands
  { value: 'gl', label: 'Galician', countryCode: 'es' }, // Spain
  { value: 'ka', label: 'Georgian', countryCode: 'ge' }, // Georgia
  { value: 'de', label: 'German', countryCode: 'de' }, // Germany
  { value: 'el', label: 'Greek', countryCode: 'gr' }, // Greece
  { value: 'gn', label: 'Guarani', countryCode: 'py' }, // Paraguay
  { value: 'gu', label: 'Gujarati', countryCode: 'in' }, // India
  { value: 'ht', label: 'Haitian Creole', countryCode: 'ht' }, // Haiti
  { value: 'ha', label: 'Hausa', countryCode: 'ng' }, // Nigeria
  { value: 'haw', label: 'Hawaiian', countryCode: 'us' }, // United States (Hawaii)
  { value: 'he', label: 'Hebrew', countryCode: 'il' }, // Israel
  { value: 'hi', label: 'Hindi', countryCode: 'in' }, // India
  { value: 'hmn', label: 'Hmong', countryCode: 'xx' }, // Not country-specific
  { value: 'hu', label: 'Hungarian', countryCode: 'hu' }, // Hungary
  { value: 'is', label: 'Icelandic', countryCode: 'is' }, // Iceland
  { value: 'ig', label: 'Igbo', countryCode: 'ng' }, // Nigeria
  { value: 'ilo', label: 'Ilocano', countryCode: 'ph' }, // Philippines
  { value: 'id', label: 'Indonesian', countryCode: 'id' }, // Indonesia
  { value: 'ga', label: 'Irish', countryCode: 'ie' }, // Ireland
  { value: 'it', label: 'Italian', countryCode: 'it' }, // Italy
  { value: 'ja', label: 'Japanese', countryCode: 'jp' }, // Japan
  { value: 'jv', label: 'Javanese', countryCode: 'id' }, // Indonesia
  { value: 'kn', label: 'Kannada', countryCode: 'in' }, // India
  { value: 'kk', label: 'Kazakh', countryCode: 'kz' }, // Kazakhstan
  { value: 'km', label: 'Khmer', countryCode: 'kh' }, // Cambodia
  { value: 'rw', label: 'Kinyarwanda', countryCode: 'rw' }, // Rwanda
  { value: 'gom', label: 'Konkani', countryCode: 'in' }, // India
  { value: 'ko', label: 'Korean', countryCode: 'kr' }, // South Korea
  { value: 'kri', label: 'Krio', countryCode: 'sl' }, // Sierra Leone
  { value: 'ku', label: 'Kurdish', countryCode: 'iq' }, // Iraq (Kurdistan)
  { value: 'ckb', label: 'Kurdish (Sorani)', countryCode: 'iq' }, // Iraq (Kurdistan)
  { value: 'ky', label: 'Kyrgyz', countryCode: 'kg' }, // Kyrgyzstan
  { value: 'lo', label: 'Lao', countryCode: 'la' }, // Laos
  { value: 'la', label: 'Latin', countryCode: 'xx' }, // Not country-specific
  { value: 'lv', label: 'Latvian', countryCode: 'lv' }, // Latvia
  { value: 'ln', label: 'Lingala', countryCode: 'cg' }, // Congo
  { value: 'lt', label: 'Lithuanian', countryCode: 'lt' }, // Lithuania
  { value: 'lg', label: 'Luganda', countryCode: 'ug' }, // Uganda
  { value: 'lb', label: 'Luxembourgish', countryCode: 'lu' }, // Luxembourg
  { value: 'mk', label: 'Macedonian', countryCode: 'mk' }, // North Macedonia
  { value: 'mai', label: 'Maithili', countryCode: 'in' }, // India
  { value: 'mg', label: 'Malagasy', countryCode: 'mg' }, // Madagascar
  { value: 'ms', label: 'Malay', countryCode: 'my' }, // Malaysia
  { value: 'ml', label: 'Malayalam', countryCode: 'in' }, // India
  { value: 'mt', label: 'Maltese', countryCode: 'mt' }, // Malta
  { value: 'mi', label: 'Maori', countryCode: 'nz' }, // New Zealand
  { value: 'mr', label: 'Marathi', countryCode: 'in' }, // India
  { value: 'mni-Mtei', label: 'Meiteilon (Manipuri)', countryCode: 'in' }, // India
  { value: 'lus', label: 'Mizo', countryCode: 'in' }, // India
  { value: 'mn', label: 'Mongolian', countryCode: 'mn' }, // Mongolia
  { value: 'my', label: 'Myanmar (Burmese)', countryCode: 'mm' }, // Myanmar
  { value: 'ne', label: 'Nepali', countryCode: 'np' }, // Nepal
  { value: 'no', label: 'Norwegian', countryCode: 'no' }, // Norway
  { value: 'ny', label: 'Nyanja (Chichewa)', countryCode: 'mw' }, // Malawi
  { value: 'or', label: 'Odia (Oriya)', countryCode: 'in' }, // India
  { value: 'om', label: 'Oromo', countryCode: 'et' }, // Ethiopia
  { value: 'ps', label: 'Pashto', countryCode: 'af' }, // Afghanistan
  { value: 'fa', label: 'Persian', countryCode: 'ir' }, // Iran
  { value: 'pl', label: 'Polish', countryCode: 'pl' }, // Poland
  { value: 'pt', label: 'Portuguese', countryCode: 'pt' }, // Portugal
  { value: 'pa', label: 'Punjabi', countryCode: 'in' }, // India
  { value: 'qu', label: 'Quechua', countryCode: 'pe' }, // Peru
  { value: 'ro', label: 'Romanian', countryCode: 'ro' }, // Romania
  { value: 'ru', label: 'Russian', countryCode: 'ru' }, // Russia
  { value: 'sm', label: 'Samoan', countryCode: 'ws' }, // Samoa
  { value: 'sa', label: 'Sanskrit', countryCode: 'in' }, // India
  { value: 'gd', label: 'Scots Gaelic', countryCode: 'gb' }, // Scotland
  { value: 'nso', label: 'Sepedi', countryCode: 'za' }, // South Africa
  { value: 'sr', label: 'Serbian', countryCode: 'rs' }, // Serbia
  { value: 'st', label: 'Sesotho', countryCode: 'za' }, // South Africa
  { value: 'sn', label: 'Shona', countryCode: 'zw' }, // Zimbabwe
  { value: 'sd', label: 'Sindhi', countryCode: 'pk' }, // Pakistan
  { value: 'si', label: 'Sinhala', countryCode: 'lk' }, // Sri Lanka
  { value: 'sk', label: 'Slovak', countryCode: 'sk' }, // Slovakia
  { value: 'sl', label: 'Slovenian', countryCode: 'si' }, // Slovenia
  { value: 'so', label: 'Somali', countryCode: 'so' }, // Somalia
  { value: 'es', label: 'Spanish', countryCode: 'es' }, // Spain
  { value: 'su', label: 'Sundanese', countryCode: 'id' }, // Indonesia
  { value: 'sw', label: 'Swahili', countryCode: 'ke' }, // Kenya
  { value: 'sv', label: 'Swedish', countryCode: 'se' }, // Sweden
  { value: 'tl', label: 'Tagalog (Filipino)', countryCode: 'ph' }, // Philippines
  { value: 'tg', label: 'Tajik', countryCode: 'tj' }, // Tajikistan
  { value: 'ta', label: 'Tamil', countryCode: 'in' }, // India
  { value: 'tt', label: 'Tatar', countryCode: 'ru' }, // Russia
  { value: 'te', label: 'Telugu', countryCode: 'in' }, // India
  { value: 'th', label: 'Thai', countryCode: 'th' }, // Thailand
  { value: 'ti', label: 'Tigrinya', countryCode: 'er' }, // Eritrea
  { value: 'ts', label: 'Tsonga', countryCode: 'za' }, // South Africa
  { value: 'tr', label: 'Turkish', countryCode: 'tr' }, // Turkey
  { value: 'tk', label: 'Turkmen', countryCode: 'tm' }, // Turkmenistan
  { value: 'ak', label: 'Twi (Akan)', countryCode: 'gh' }, // Ghana
  { value: 'uk', label: 'Ukrainian', countryCode: 'ua' }, // Ukraine
  { value: 'ur', label: 'Urdu', countryCode: 'pk' }, // Pakistan
  { value: 'ug', label: 'Uyghur', countryCode: 'cn' }, // China
  { value: 'uz', label: 'Uzbek', countryCode: 'uz' }, // Uzbekistan
  { value: 'vi', label: 'Vietnamese', countryCode: 'vn' }, // Vietnam
  { value: 'cy', label: 'Welsh', countryCode: 'gb' }, // United Kingdom (Wales)
  { value: 'xh', label: 'Xhosa', countryCode: 'za' }, // South Africa
  { value: 'yi', label: 'Yiddish', countryCode: 'xx' }, // Not country-specific
  { value: 'yo', label: 'Yoruba', countryCode: 'ng' }, // Nigeria
  { value: 'zu', label: 'Zulu', countryCode: 'za' }, // South Africa
];
export const languageItems = languages.map((l) => ({ ...l, image: `https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/7.2.3/flags/4x3/${l.countryCode}.svg` }));
export const addMetaTag = () => {
  if (process.env.REACT_APP_STAGE !== "production") {
    const existingMetaTags = document.querySelectorAll("meta[name='robots']");
    if (!existingMetaTags.length) {
      const meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      meta.setAttribute("content", "noindex, nofollow");
      document.head.appendChild(meta);
    }
  }
};

export const capitalize = (str, lower = false) =>
  ((lower ? str.toLowerCase() : str) || "").replace(/(^|\s)\S/g, (match) => match.toUpperCase());
  
export const formatUSPhoneNumber = (number) => {
  if (number) {
    const formatted = phoneFormatter.format(number, "+1NNNNNNNNNN");
    if (verifyPhoneFormat(formatted)) return formatted;
  }
  return "";
};

export const formatUSPhoneNumberPretty = (phoneNumber) => {
  if (phoneNumber) {
    const PHONE_NUMBER_FORMAT = "(NNN) NNN-NNNN";
    phoneNumber = phoneNumber.replace("+1", "");
    if (phoneNumber.length === 10) return phoneFormatter.format(phoneNumber, PHONE_NUMBER_FORMAT);
    return phoneNumber;
  }
  return "";
};
export const verifyEmailFormat = (email) => {
  const patt = new RegExp(/[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/g);
  return patt.test(email);
};

export const verifyPhoneFormat = (phone) => {
  const patt = new RegExp(/^[+]?(\d{1,2})?[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/);
  return patt.test(phone);
};

export const verifyDomainFormat = (domain) => {
  const domainPattern = /^(?!:\/\/)([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,}$/;
  return domainPattern.test(domain);
};

export const verifyURLFormat = (url) => {
  const urlPattern = /^(https?:\/\/)?((([a-z\d]([a-z\d-]*[a-z\d])*)\.)+[a-z]{2,}|((\d{1,3}\.){3}\d{1,3}))(:\d+)?(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(#[-a-z\d_]*)?$/i;
  return urlPattern.test(url);
};

export const allowNumbersOnly = (e) => {
  const code = (e.which) ? e.which : e.keyCode;
  if (code > 31 && (code < 48 || code > 57)) e.preventDefault();
};

export const allowNumbersAndDecimalsOnly = (e) => {
  const key = e.key || e.charCode;
  const isValidInput = /^\d$/.test(key) || key === '.'; // Check if the key is a number or a decimal point

  if (!isValidInput) {
    e.preventDefault(); // Prevent default behavior if the input is not valid
  }
};

export const limitNumberRange = (e, min, max) => {
  if (e.target.value < min) e.target.value = min;
  if (e.target.value > max) e.target.value = max;
};

export const limitInput = (event, limit) => {
  if ((limit > 0) && event.target.value.length > limit) event.preventDefault();
};

export const uniqueID = (delimiter = "_", length = 6, type = "both") => {
  let chars = "";

  // Define character sets based on the type
  if (type === "numbers") {
    chars = "0123456789";
  } else if (type === "letters") {
    chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
  } else {
    chars = "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
  }

  // Generate random ID from the chosen characters
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return delimiter + result;
};

export const generatePassword = (length = 8) => {
  let charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let retVal = "";
  for (var i = 0, n = charset.length; i < length; ++i) retVal += charset.charAt(Math.floor(Math.random() * n));
  return retVal;
};

export const getReadableFileSizeString = (fileSizeInBytes, granular) => {
  let i = -1;
  const byteUnits = ["KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  do {
    fileSizeInBytes = fileSizeInBytes / 1024;
    i++;
  } while (fileSizeInBytes > 1024);
  return `${Math.max(fileSizeInBytes, 0.1).toFixed(granular ? 2 : 0)} ${byteUnits[i]}`;
};
export const getReadableUserAddress = (user) => {
  let user_address = [];
  if (user.address) user_address.push(user.address);
  if (user.address2) user_address.push(user.address2);
  if (user.city) user_address.push(user.city);
  if (user.state) user_address.push(user.state);
  if (user.zip) user_address.push(user.zip);
  if (user_address.length) return user_address.join(", ");
  return false;
};
export const sleep = (ms) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

const image_ocr_types = [
  "png",
  "jpg",
  "jpeg",
  "tiff",
  "bmp",
  "gif",
  "ico"
];

export const doc_ocr_types = [
  "pdf",
  "doc",
  "docx",
  "vnd.openxmlformats-officedocument.wordprocessingml.document",
  "xls",
  "xlsx",
  "json",
  "txt",
  "rtf",
  "csv",
  "html",
  "xml"
];

export const audio_ocr_types = ["mp3", "wav", "m4a", "ogg", "webm", "aac", "flac", "mpeg"];

export const ocr_types = [...image_ocr_types, ...doc_ocr_types, ...audio_ocr_types];
export const getCardIcon = (type) => {
  switch (type) {
    case "amex":
      return { icon: "cc-amex", icon_type: "fab", color: "#336cae" };
    case "cartes bancaires":
      return { icon: "credit-card", icon_type: "fad", color: "#309469" };
    case "diners":
      return { icon: "cc-diners-club", icon_type: "fab", color: "#2d6aa6" };
    case "discover":
      return { icon: "cc-discover", icon_type: "fab", color: "#E55C20" };
    case "jcb":
      return { icon: "cc-jcb", icon_type: "fab", color: "#F9B101" };
    case "mastercard":
      return { icon: "cc-mastercard", icon_type: "fab", color: "#eb001b" };
    case "visa":
      return { icon: "cc-visa", icon_type: "fab", color: "#172274" };
    case "unionpay":
      return { icon: "credit-card-front", icon_type: "fad", color: "#327982" };
    default:
      return { icon: "credit-card", icon_type: "fad", color: null };
  }
};

export const extractNames = (obj) => {
  const names = [];
  for (const key in obj) {
    if (obj[key].hasOwnProperty("sub_items")) {
      const subItems = obj[key].sub_items;
      for (const subKey in subItems) {
        const page = subItems[subKey].page;
        if (page && page.show) {
          names.push(page.name);
        }
      }
    }
  }
  return names;
};

export const groupBy = (array, keyFn) => {
  return array.reduce((result, item) => {
    const key = keyFn(item);
    if (!result[key]) {
      result[key] = [];
    }
    result[key].push(item);
    return result;
  }, {});
};

export const toDataURL = async (src, blobOnly = false, bufferOnly = false) => {
  let data = await fetch(src).then(async (r) => {
    const buffer = await r.arrayBuffer();
    return {
      buffer,
      blob: new Blob([buffer], { type: r.headers.get("content-type") }),
    };
  });
  let d = await new Promise((resolve) => {
    let reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(data.blob);
  });
  if (bufferOnly && !blobOnly) return data.buffer;
  if (blobOnly && !bufferOnly) return data.blob;
  if (blobOnly && bufferOnly) return data;
  return d;
};

export const getUserAge = (birthday) => {
  if (!birthday) return 0;
  const parsedBirthday = new Date(birthday);
  if (isNaN(parsedBirthday.getTime())) return 0;
  const ageDifMs = Date.now() - parsedBirthday.getTime();
  const ageDate = new Date(ageDifMs); // Convert the age difference to a date
  const age = Math.abs(ageDate.getUTCFullYear() - 1970); // Get the absolute difference in years
  return age;
};
export const mergeArrays = (array1, array2, key) => {
  const resultArray = [...array1];
  for (const item of array2) {
    const index = resultArray.findIndex((element) => element[key] === item[key]);
    if (index !== -1) resultArray[index] = item;
    else resultArray.push(item);
  }
  return resultArray;
};

// Truncation helpers export
export { midTruncate, TruncatedId };

export const toSentenceCase = (str) => {
  if (!str) return '';
  return str
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/(^\w|[.!?]\s*\w)/g, (c) => c.toUpperCase());
};

export const search = (fields, target, term, returning) => {
  if (!term) return [];
  const lowSearch = term.toLowerCase();
  const recursiveSearch = (item, keys) => {
    let index = 0;
    while (index < keys.length) {
      const key = keys[index];
      if (item[key]) {
        item = item[key];
        index++;
      } else {
        return false;
      }
    }
    return String(item).toLowerCase().includes(lowSearch);
  };
  return target.filter(function (item) {
    return fields.some((key) => {
      let string = item[key];
      if (key.includes(".")) {
        let nested_key = key.split(".");
        return recursiveSearch(item, nested_key);
      }
      return String(string).toLowerCase().includes(lowSearch);
    });
  }).map((item) => !returning ? item : item[returning]);
};

export const wordsToID = (string, delimiter = "_") => {
  if (string === null || string === undefined) {
    throw new Error("Input cannot be null or undefined");
  }
  
  if (typeof string !== "string") {
    throw new TypeError("Input must be a string");
  }
  
  const trimmedString = string.trim();
  const lowercaseString = trimmedString.toLowerCase();
  
  const replaced = lowercaseString.replace(/[^a-z0-9]/gi, delimiter);
  return replaced;
};

export const idToWords = (id, delimiter = "_") => {
  if (id === null || id === undefined) {
    throw new Error("Input cannot be null or undefined");
  }

  if (typeof id !== "string") {
    throw new TypeError("Input must be a string");
  }

  const replaced = id.replace(new RegExp(delimiter, "g"), " ");
  return replaced;
};

export const getRandomNeutralColor = () => {
  const min = 100; // Minimum value for each RGB component
  const max = 200; // Maximum value for each RGB component

  // Generate random values for red, green, and blue components
  const red = Math.floor(Math.random() * (max - min + 1)) + min;
  const green = Math.floor(Math.random() * (max - min + 1)) + min;
  const blue = Math.floor(Math.random() * (max - min + 1)) + min;

  // Construct the color string in the format 'rgb(r, g, b)'
  return `rgb(${red}, ${green}, ${blue})`;
};

const getMillisecondsPerUnit = (unit) => {
  const unitsInMilliseconds = {
    year: 31536000000,
    month: 2592000000,
    week: 604800000,
    day: 86400000,
    hour: 3600000,
    minute: 60000,
    second: 1000,
  };
  return unitsInMilliseconds[unit] || 1; // Default to 1 millisecond for unknown units
};

export const getURLParams = (key) => {
  const searchParams = new URLSearchParams(window.location.search);
  if (key) return searchParams.get(key);
  else return searchParams;
};

export const isTimeAgo = (amount, unit, timestamp) => {
  if (!timestamp) return false;
  const currentTimestamp = new Date().getTime();
  const targetTimestamp = new Date(timestamp).getTime();

  if (isNaN(targetTimestamp)) return false;
  const timeDifference = currentTimestamp - targetTimestamp;
  const millisecondsPerUnit = getMillisecondsPerUnit(unit);
  const timeAgoThreshold = amount * millisecondsPerUnit;
  return timeDifference > timeAgoThreshold;
};

export const checkOnlineStatus = () => {

  const runOffline = () => {
    store.dispatch({ type: UPDATE_ONLINE_STATUS, payload: false })
    toastr.error("You are offline.", "Looks like you're not connected to the internet. Please check your settings and try again.", {
      timeOut: null,
      attention: true,
      onAttentionClick: (id) => { },
      preventDuplicates: true,
      newestOnTop: true,
      showCloseButton: false,
      removeOnHover: false,
      closeOnToastrClick: false,
      closeOnShadowClick: false,
      tapToDismiss: false
    });
    toastr.removeByType("success");
  };

  const runOnline = () => {
    store.dispatch({ type: UPDATE_ONLINE_STATUS, payload: true })
    toastr.success("You are back online.", "Looks like you're back online and your connection is stable.", { timeOut: 10000 });
    toastr.removeByType("error");
  };

  window.addEventListener("online", () => runOnline(), { once: true });
  window.addEventListener("offline", () => runOffline(), { once: true });

  if (navigator?.onLine) {
    store.dispatch({ type: UPDATE_ONLINE_STATUS, payload: true });
    toastr.removeByType("error");
    return true;
  } else {
    runOffline();
    return false;
  }
};

export const surveys = {
  partner_project_creation: "66cc8e80-a6f1-4b3d-818c-93581fd78a4a",
  project_creation: "e85be630-5631-411f-bedf-9ecda1422e1b",
  registration: "a45c0fac-2310-4993-877a-105ab6e60092",
  partner_registration: "1e3daaae-452e-45f9-9837-cb6a998f882a",
  invites: "d017bd03-4239-40e3-8e66-fc16443aed8a",
  forgot: "81a93236-4505-4b0e-8f20-4f4b88790728"
};

// Operation counter to avoid race condition toasts when user toggles push rapidly
let __pushOpCounter = 0;

export const requestPermission = async (reset, refreshUser = true) => {
  const { user } = store.getState().authentication;
  const { account } = store.getState().session;
  const opId = ++__pushOpCounter; // this enable attempt id

  if (reset) await unsubscribe(true);

  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      debug.warn("Service worker or Push manager not supported");
      return false;
    }

    const registration = await navigator.serviceWorker.register("service-worker.js");

    if (registration.active) {
      // Service worker is already active
    } else {
      // Wait for service worker to become active
      await new Promise(resolve => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'activated') {
                resolve();
              }
            });
          }
        });
      });
    }

    store.dispatch({ type: WORKER_ACTIVE, payload: true });

    if (Notification.permission !== "granted") {
      debug.log('ui', 'Requesting notification permission', { current: Notification.permission });
      const permissionResult = await Notification.requestPermission();
      if (permissionResult !== "granted") {
        debug.warn("Notification permission denied", { result: permissionResult });
        return false;
      }
      debug.log('ui', 'Notification permission granted', { result: permissionResult });
    }

    const subscribeOptions = {
      userVisibleOnly: true,
      applicationServerKey: (() => {
        let key = process.env.REACT_APP_PUBLIC_VAPID_KEY;
        if (!key && typeof window !== 'undefined' && window.__ENV && window.__ENV.REACT_APP_PUBLIC_VAPID_KEY) {
          key = window.__ENV.REACT_APP_PUBLIC_VAPID_KEY;
        }
        if (!key) {
          debug.error('[push] Missing REACT_APP_PUBLIC_VAPID_KEY env var');
          return null;
        }
        try {
          const padding = '='.repeat((4 - (key.length % 4)) % 4);
          const base64 = (key.replace(/-/g, '+').replace(/_/g, '/') + padding);
          const raw = atob(base64);
          const output = new Uint8Array(raw.length);
            for (let i=0;i<raw.length;i++) output[i] = raw.charCodeAt(i);
          if (output.length !== 65) debug.warn('VAPID key length unexpected (expected 65 bytes)', { length: output.length });
          return output;
        } catch (e) {
          debug.error('Failed to decode VAPID key', e);
          return null;
        }
      })(),
    };
    if (!subscribeOptions.applicationServerKey) {
      debug.error('No valid applicationServerKey after VAPID conversion');
      return false;
    }

    const device = { ...deviceDetect(), deviceType };
    const pushManager = registration.pushManager;
    const pushSubscription = await pushManager.subscribe(subscribeOptions);

    let device_id = store.getState().session.device_id;
    if (!device_id) {
      device_id = v4();
      store.dispatch({ type: UPDATE_DEVICE_ID, payload: device_id });
    }
    const created = await createPushSubscription(pushSubscription, { device, device_id });

    if (created.success) {
      if (refreshUser) await getUser(user.cognito_id, account.account_id, true);
      // Only show success if this is still the latest operation (wasn't invalidated by a disable)
      if (opId === __pushOpCounter) {
        toastr.success("Push Notifications Active", "Push notifications successfully enabled on this device.");
      } else {
        // Suppressed stale success toast after disable
      }
    } else {
      debug.error("Push Notifications Inactive", created.message);
      toastr.error("Push Notifications Inactive", created.message);
    }

    return created.success;
  } catch (error) {
    debug.error("Service Worker Registration Error:", error.message);
    store.dispatch({ type: WORKER_ACTIVE, payload: false });
    return false;
  }
};

export const unsubscribe = async (force = false) => {
  const { user } = store.getState().authentication;
  const { account } = store.getState().session;

  try {
    if (!('serviceWorker' in navigator)) {
      debug.warn("Service worker not supported in browser");
      return false;
    }

    if (!('PushManager' in window)) {
      debug.warn("Push manager not supported in browser");
      return false;
    }

    if (Notification?.permission !== "granted") {
      debug.log('ui', 'Push notification permission not granted', { permission: Notification.permission });
      return false;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();

    if (!registrations.length) {
      debug.log('ui', "No registered service workers. Registering...");
      await navigator.serviceWorker.register("service-worker.js");
    }
    
    const reg = await navigator.serviceWorker.ready;
    const subscription = await reg.pushManager.getSubscription();
    if (!subscription) {
      debug.log('ui', 'No push subscription found');
      return false;
    }

  await subscription.unsubscribe();
  // Invalidate any in-flight enable attempt so its success toast is suppressed
  __pushOpCounter++;
    
    const updated = await updatePushSubscription(subscription, { status: "inactive" });
    await getUser(user.cognito_id, account.account_id, true);
    store.dispatch({ type: UPDATE_DEVICE_ID, payload: null });

    if (updated.success) {
      if (!force) toastr.error("Push Notifications Disabled", "Push notifications have been disabled on this device.");
    } else {
      debug.error("Push Error", updated.message);
    }

    return updated;
  } catch (error) {
    debug.error("Push Error", error);
    return false;
  }
};

export const checkPushSubscription = async () => {
  if (!('serviceWorker' in navigator)) {
    debug.error("Service worker not supported");
    return false;
  }

  if (!('PushManager' in window)) {
    debug.error("Push manager not supported");
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription;
  } catch (error) {
    debug.error('Push unsubscribe error', error);
    return false;
  }
};

export const NotificationIsSupported = 'Notification' in window || 'webkitNotifications' in window || 'mozNotification' in navigator;

export const isInStandaloneMode = () =>
  (window.matchMedia("(display-mode: standalone)").matches) || (window.navigator.standalone) || document.referrer.includes("android-app://");

export const fileToBlob = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      resolve(event.target.result);
    };
    reader.onerror = (error) => {
      debug.error('FileReader error', error);
      reject(error);
    };
    reader.readAsDataURL(file);
  });
};

const arrayBufferToBase64 = (ab) => {
  const CHUNK = 0x8000; // 32k
  const u8 = new Uint8Array(ab);
  let i = 0;
  let base64 = '';
  while (i < u8.length) {
    const chunk = u8.subarray(i, i + CHUNK);
    base64 += String.fromCharCode.apply(null, chunk);
    i += CHUNK;
  }
  return btoa(base64);
};

export const blobToDataURL = async (input) => {
  if (!input) throw new Error('blobToDataURL: missing input');

  // If string -> fetch and convert, but validate content-type (avoid HTML)
  if (typeof input === 'string') {
    const resp = await fetch(input, { credentials: 'same-origin' });
    const ct = resp.headers.get('content-type') || '';
    if (ct.startsWith('text/html')) {
      throw new Error(`blobToDataURL: URL returned HTML (content-type ${ct})`);
    }
    const ab = await resp.arrayBuffer();
    const base64 = await arrayBufferToBase64(ab);
    return `data:${ct || 'application/octet-stream'};base64,${base64}`;
  }

  // If Blob or File -> use FileReader (fast & reliable)
  if (input instanceof Blob) {
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = (e) => reject(new Error('blobToDataURL: FileReader failed'));
      fr.readAsDataURL(input);
    });
  }

  throw new TypeError('blobToDataURL: input must be a Blob/File or URL string');
};

export const fileToBase64 = async (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (e) => reject(e)
  })

export const getCurrentTimezone = () => {
  const timeZoneOffset = new Date().getTimezoneOffset();
  const full = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const time_parts = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })?.formatToParts(new Date());
  const abbreviation = time_parts.find((part) => part.type === "timeZoneName").value;
  return { full, abbreviation, offset: timeZoneOffset };
};

export const dataURItoBlob = (dataurl) => {
  if (dataurl) {
    var arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }
  return null;
};

export const replaceFrom24To12Format = (match, hours, minutes, period) => {
  if (period) return `${hours}:${minutes} ${period}`;
  const periodToAdd = hours >= 12 ? " PM" : " AM";
  hours = (hours % 12) || 12;
  return `${hours}:${minutes}${periodToAdd}`;
};

export const convertFrom24To12Format = (time24) => {
  const [sHours, minutes] = time24.match(/([0-9]{1,2}):([0-9]{2})/).slice(1);
  const period = +sHours < 12 ? "AM" : "PM";
  const hours = +sHours % 12 || 12;
  return `${hours}:${minutes} ${period}`;
};

export const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return false;
  if (Array.isArray(value)) return false;
  if (typeof value === "number") return false;
  if (typeof value === "object") return false;
  if (typeof value === "boolean") return false;
  return false;
};

export const updateURLParams = (query_updates, callback) => {
  const currentSearchParams = new URLSearchParams(window.location.search);
  Object.keys(query_updates).forEach((key) => {
    const oldQuery = currentSearchParams.get(key) ?? '';
    const newQuery = query_updates[key];
    if (newQuery && oldQuery && (newQuery === oldQuery)) return;

    if (newQuery) {
      currentSearchParams.set(key, newQuery)
    } else {
      currentSearchParams.delete(key)
    }
  });
  const newUrl = [window.location.pathname, currentSearchParams.toString()]
    .filter(Boolean)
    .join('?')
  window.history.replaceState(null, '', newUrl);
  if (callback) callback();
};

export const scrollTo = (h, offset) => {
  if (h === "") {
    window.scrollTo(0, 0);
  } else {
    setTimeout(() => {
      const id = h.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center", offset });
        window.history.pushState("", document.title, window.location.pathname + window.location.search);
      }
    }, 120);
  }
};

export const clearObjectStore = (dbName, objectStoreName) => {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(dbName);

    request.onsuccess = (event) => {
      const db = event.target.result;
      
      // Check if the object store exists before attempting to clear it
      if (!db.objectStoreNames.contains(objectStoreName)) {
        debug.warn(`Object store "${objectStoreName}" not found in database "${dbName}". Skipping clear.`);
        db.close();
        resolve();
        return;
      }

      try {
        const transaction = db.transaction(objectStoreName, 'readwrite');
        const objectStore = transaction.objectStore(objectStoreName);

        const clearRequest = objectStore.clear();

        clearRequest.onsuccess = () => {
          db.close();
          resolve();
        };

        clearRequest.onerror = (event) => {
          debug.error(`Error clearing data in object store ${objectStoreName}:`, event.target.error);
          db.close();
          reject(event.target.error);
        };

        transaction.onerror = (event) => {
          debug.error(`Transaction error clearing ${objectStoreName}:`, event.target.error);
          db.close();
          reject(event.target.error);
        };
      } catch (err) {
        debug.error(`Exception clearing object store ${objectStoreName}:`, err);
        db.close();
        reject(err);
      }
    };

    request.onerror = (event) => {
      debug.error('Error opening IndexedDB for clearing data', { database: dbName, error: event.target.error });
      reject(event.target.error);
    };
  });
};

export const limitConcurrency = async (items, concurrencyLimit, asyncTask) => {
  const results = [];
  let currentIndex = 0;
  const processingQueue = [];

  const processNext = async () => {
    const item = items[currentIndex++];
    if (!item) return; // No more items to process

    const result = await asyncTask(item);
    results.push(result);

    // Remove the completed item from the processing queue
    const indexToRemove = processingQueue.indexOf(item);
    if (indexToRemove !== -1) {
      processingQueue.splice(indexToRemove, 1);
    }

    // Start processing the next item
    await processNext();
  };

  const processItems = async () => {
    while (currentIndex < items.length || processingQueue.length > 0) {
      while (currentIndex < items.length && processingQueue.length < concurrencyLimit) {
        const item = items[currentIndex++];
        processingQueue.push(item);
        processNext(); // Start processing the item
      }
      await Promise.all(processingQueue.map(() => new Promise((resolve) => setTimeout(resolve, 100))));
    }
  };

  // Start processing the items
  await processItems();

  return results;
};

export const waitForCondition = async (checkFunction, intervalMs, maxIterations, failureFunction) => {
  return new Promise((resolve, reject) => {
    let iterations = 0;

    const intervalId = setInterval(async () => {
      iterations++;

      if (await checkFunction()) {
        clearInterval(intervalId);
        resolve();
      } else if (maxIterations && iterations >= maxIterations) {
        clearInterval(intervalId);
        if (failureFunction) failureFunction();
        reject(new Error("Max iterations reached"));
      }
    }, intervalMs);
  });
};

export const inSequence = async (array) => {
  for (const promise of array) {
    await promise;
  }
};

export const checkUntil = (options) => {
  const {
    localStorageKey,
    maxIterations,
    intervalMs,
    startCondition,
    onSuccess,
  } = options;

  if (!startCondition(localStorage.getItem(localStorageKey))) {
    return false;
  }

  let currentIteration = 0;

  const checkInterval = setInterval(() => {
    currentIteration++;
    const storedValue = localStorage.getItem(localStorageKey);

    if (storedValue) {
      clearInterval(checkInterval);
      if (onSuccess) {
        onSuccess(storedValue);
        return true;
      }
    } else if (currentIteration >= maxIterations) {
      clearInterval(checkInterval);
    }
  }, intervalMs);
};

export const getJSONFromURL = async (url) => {
  const response = await fetch(url);
  return response.json();
};

export const sortByTime = (a, b) => {
  const timeA = a.raw.split(':').map(Number);
  const timeB = b.raw.split(':').map(Number);

  if (timeA[0] !== timeB[0]) {
    return timeA[0] - timeB[0]; // Compare hours
  } else {
    return timeA[1] - timeB[1]; // If hours are equal, compare minutes
  }
};

export const getOpenAIStatus = async () => {
  const url = "https://status.openai.com/api/v2/summary.json";
  const response = await getJSONFromURL(url);
  const { incidents = [], status } = response;
  const API = response.components.find((c) => c.id === "scd0s93nldpb")
  const latestIncident = incidents?.[0];
  if (!latestIncident) {
    return {
      API,
      last_check: Date.now(),
      latestIncident: null,
      status
    };
  }
  return {
    API,
    last_check: Date.now(),
    latestIncident,
    status
  };
};

export const getAblyStatus = async () => {
  const url = "https://ably-realtime.statuspage.io/api/v2/summary.json";
  const response = await getJSONFromURL(url);
  const { incidents = [], status } = response;
  const API = response.components.find((c) => c.id === "f7yl2s70zbtj")
  const latestIncident = incidents?.[0];
  if (!latestIncident) {
    return {
      API,
      last_check: Date.now(),
      latestIncident: null,
      status
    };
  }
  return {
    API,
    last_check: Date.now(),
    latestIncident,
    status
  };
};

export const resizeFile = (file, width = 300, height = 300, compression = "JPEG", quality = 100, rotation = 0, type = "base64") =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      width,
      height,
      compression,
      quality,
      rotation,
      (uri) => {
        resolve(uri);
      },
      type
    );
  });
export const checkConditions = (input, conditions) => {
  for (const condition of conditions) {
    if (!condition(input)) {
      return false;
    }
  }
  return true;
};

const permission_suggestions = {
  "Brother": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Sister": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Father": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Mother": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Son": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Daughter": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Grandfather": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "contacts", "medications"],
  "Grandmother": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "contacts", "medications"],
  "Grandson": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "contacts", "medications"],
  "Granddaughter": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "contacts", "medications"],
  "Uncle": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "contacts", "medications"],
  "Aunt": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "contacts", "medications"],
  "Cousin": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "contacts", "medications"],
  "Nephew": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "contacts", "medications"],
  "Niece": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "contacts", "medications"],
  "Husband": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Wife": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Fiancé": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Partner": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Stepfather": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Stepmother": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Stepson": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Stepdaughter": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Stepbrother": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Stepsister": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Half-Brother": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Half-Sister": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Father-in-Law": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Mother-in-Law": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Brother-in-Law": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Sister-in-Law": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Son-in-Law": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Daughter-in-Law": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Godfather": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Godmother": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Godson": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Goddaughter": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Friend": ["full_plan", "knowledge_base", "plan_settings", "sharing", "contacts"],
  "Colleague": ["full_plan", "knowledge_base", "plan_settings", "sharing"],
  "Boss": ["full_plan", "knowledge_base", "plan_settings", "sharing"],
  "Manager": ["full_plan", "knowledge_base", "plan_settings", "sharing"],
  "Work Supervisor": ["full_plan", "knowledge_base", "plan_settings", "sharing"],
  "Employee": ["full_plan", "knowledge_base", "plan_settings", "sharing"],
  "Teacher": ["full_plan", "knowledge_base", "plan_settings", "sharing"],
  "Student": ["full_plan", "knowledge_base", "plan_settings", "sharing"],
  "Neighbor": ["full_plan", "knowledge_base", "plan_settings", "sharing"],
  "Roommate": ["full_plan", "knowledge_base", "plan_settings", "sharing", "members", "contacts"],
  "Guardian": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Attorney": ["documents"],
  "Case Manager": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "members", "contacts", "medications"],
  "Client": ["full_plan", "knowledge_base"],
  "Patient": ["full_plan", "knowledge_base"],
  "Mentor": ["full_plan", "knowledge_base", "plan_settings", "sharing"],
  "Mentee": ["full_plan", "knowledge_base"],
  "Associate": ["full_plan", "knowledge_base"],
  "Supervisor": ["full_plan", "knowledge_base", "plan_settings", "sharing"],
  "Intern": ["full_plan", "knowledge_base"],
  "Coach": ["full_plan", "knowledge_base", "plan_settings", "sharing"],
  "Advisor": ["full_plan", "knowledge_base", "plan_settings", "sharing"],
  "Consultant": ["full_plan", "knowledge_base"],
  "Volunteer": ["full_plan", "knowledge_base"],
  "Vendor": ["full_plan", "knowledge_base"],
  "Contractor": ["full_plan", "knowledge_base"],
  "Landlord": ["full_plan", "knowledge_base", "documents"],
  "Tenant": ["full_plan", "knowledge_base"],
  "Caregiver": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "members", "contacts", "medications"],
  "Professional Caregiver": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "members", "contacts", "medications"],
  "Supervisee": ["full_plan", "knowledge_base"],
  "Patient Advocate": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "members", "contacts", "medications"],
  "Caseworker": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "members", "contacts", "medications"],
  "Social Worker": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "members", "contacts", "medications"],
  "Counselor": ["full_plan", "knowledge_base", "plan_settings", "sharing"],
  "Nurse": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pharmacist": ["medications"],
  "Care Coordinator": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "members", "contacts", "medications"],
  "Peer Support": ["full_plan", "knowledge_base"],
  "Instructor": ["full_plan", "knowledge_base"],
  "Classmate": ["full_plan", "knowledge_base"],
  "Teammate": ["full_plan", "knowledge_base"],
  "Sponsor": ["full_plan", "knowledge_base"],
  "Nanny": ["full_plan", "knowledge_base", "contacts", "medications"],
  "Girlfriend": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Boyfriend": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Babysitter": ["full_plan", "knowledge_base", "contacts", "medications"],
  "Foster Parent": ["full_plan", "knowledge_base", "plan_settings", "sharing", "members", "contacts", "medications"],
  "Beneficiary": ["full_plan", "knowledge_base", "documents"],
  "Recruiter": ["full_plan", "knowledge_base", "documents"],
  "Judge": ["documents"],
  "Mediator": ["documents"],
  "Occupational Therapist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Physical Therapist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Speech-Language Pathologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Respiratory Therapist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Dietitian": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Chiropractor": ["full_plan", "knowledge_base", "plan_settings", "sharing"],
  "Board Certified Behavioral Analyst": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Massage Therapist": ["full_plan", "knowledge_base", "plan_settings", "sharing"],
  "Prosthetist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Recreational Therapist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Art Therapist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Music Therapist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Cognitive Behavioral Therapist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Dialectical Behavior Therapist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Tutor": ["full_plan", "knowledge_base"],

  // Physicians 
  "Primary Care Physician": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Cardiologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Dentist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Dermatologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Endocrinologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Gastroenterologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Hematologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Immunologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Nephrologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Neurologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Obstetrician": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Oncologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Ophthalmologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Orthopedist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Otolaryngologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pediatrician": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Psychiatrist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Therapist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pulmonologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Radiologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Rheumatologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Urologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Allergist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Anesthesiologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Cardiothoracic Surgeon": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Colon and Rectal Surgeon": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Critical Care Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Emergency Medicine Physician": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Family Medicine Physician": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Geriatrician": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Gynecologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Infectious Disease Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Intensivist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Interventional Radiologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Medical Geneticist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Neonatologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Neurosurgeon": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Occupational Medicine Physician": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pathologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Physiatrist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Plastic Surgeon": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Podiatrist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Proctologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Sleep Medicine Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Sports Medicine Physician": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Thoracic Surgeon": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Vascular Surgeon": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Adolescent Medicine Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Clinical Pharmacologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Cytopathologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Developmental-Behavioral Pediatrician": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Diabetologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Emergency Medical Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Hepatologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Internal Medicine Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Maxillofacial Surgeon": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Nuclear Medicine Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pain Management Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Sleep Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Transplant Surgeon": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Travel Medicine Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Virologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Bariatrician": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Chemical Pathologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Child and Adolescent Psychiatrist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Clinical Neurophysiologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Gynecologic Oncologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Hand Surgeon": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Hospice and Palliative Medicine Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Infectious Disease Physician": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Interventional Cardiologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Maternal-Fetal Medicine Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Molecular Geneticist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Neurodevelopmental Disabilities Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Osteopathic Physician": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pediatric Allergist/Immunologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pediatric Cardiologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pediatric Critical Care Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pediatric Emergency Medicine Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pediatric Endocrinologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pediatric Gastroenterologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pediatric Hematologist-Oncologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pediatric Infectious Disease Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pediatric Nephrologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pediatric Neurologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pediatric Pulmonologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pediatric Rheumatologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Pediatric Surgeon": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Reproductive Endocrinologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Cardiac Electrophysiologist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Dental Surgeon": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Gynecologic Surgeon": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Ophthalmic Surgeon": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Orthopedic Surgeon": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Trauma Surgeon": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Spinal Cord Injury Specialist": ["full_plan", "knowledge_base", "plan_settings", "sharing", "medications"],
  "Administrator": ["administrator", "billing", "full_plan", "knowledge_base", "plan_settings", "sharing", "assign", "documents", "members", "contacts", "medications"],
  "Billing Contact": ["billing"],
  "Technical Contact": ["full_plan", "knowledge_base", "plan_settings", "sharing", "documents", "members", "contacts"],
  "Support Contact": ["full_plan", "knowledge_base", "sharing", "documents", "contacts"],
  "Account Manager": ["full_plan", "billing", "documents", "members", "contacts", "medications"],
  "Medical Contact": ["full_plan", "knowledge_base", "documents", "medications", "contacts"]
};

export const permission_based_roles = [
  { value: "Administrator", label: "Administrator", get permissions() { return permission_suggestions[this.label]; }},
  { value: "Billing Contact", label: "Billing Contact", get permissions() { return permission_suggestions[this.label]; }},
  { value: "Technical Contact", label: "Technical Contact", get permissions() { return permission_suggestions[this.label]; }},
  { value: "Support Contact", label: "Support Contact", get permissions() { return permission_suggestions[this.label]; }},
  { value: "Account Manager", label: "Account Manager", get permissions() { return permission_suggestions[this.label]; }},
  { value: "Medical Contact", label: "Medical Contact", get permissions() { return permission_suggestions[this.label]; }}
];

export const family = [
  { value: "brother", label: "Brother", get permissions() { return permission_suggestions[this.label] || []; }},
  { value: "sister", label: "Sister", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "father", label: "Father", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "mother", label: "Mother", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "son", label: "Son", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "daughter", label: "Daughter", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "grandfather", label: "Grandfather", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "grandmother", label: "Grandmother", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "grandson", label: "Grandson", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "granddaughter", label: "Granddaughter", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "uncle", label: "Uncle", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "aunt", label: "Aunt", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "cousin", label: "Cousin", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "nephew", label: "Nephew", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "niece", label: "Niece", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "husband", label: "Husband", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "wife", label: "Wife", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "fiancé", label: "Fiancé", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "partner", label: "Partner", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "stepfather", label: "Stepfather", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "stepmother", label: "Stepmother", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "stepson", label: "Stepson", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "stepdaughter", label: "Stepdaughter", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "stepbrother", label: "Stepbrother", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "stepsister", label: "Stepsister", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "halfbrother", label: "Half-Brother", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "halfsister", label: "Half-Sister", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "father-in-law", label: "Father-in-Law", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "mother-in-law", label: "Mother-in-Law", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "brother-in-law", label: "Brother-in-Law", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "sister-in-law", label: "Sister-in-Law", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "son-in-law", label: "Son-in-Law", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "daughter-in-law", label: "Daughter-in-Law", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "godfather", label: "Godfather", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "godmother", label: "Godmother", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "godson", label: "Godson", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "goddaughter", label: "Goddaughter", get permissions() { return permission_suggestions[this.label] || []; } }
];
export const other = [
  { value: "friend", label: "Friend", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "colleague", label: "Colleague", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "boss", label: "Boss", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "manager", label: "Manager", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "work supervisor", label: "Work Supervisor", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "employee", label: "Employee", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "teacher", label: "Teacher", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "student", label: "Student", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "neighbor", label: "Neighbor", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "roommate", label: "Roommate", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "guardian", label: "Guardian", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "attorney", label: "Attorney", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "case manager", label: "Case Manager", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "client", label: "Client", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "patient", label: "Patient", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "mentor", label: "Mentor", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "mentee", label: "Mentee", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "associate", label: "Associate", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "supervisor", label: "Supervisor", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "intern", label: "Intern", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "coach", label: "Coach", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "advisor", label: "Advisor", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "consultant", label: "Consultant", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "volunteer", label: "Volunteer", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "vendor", label: "Vendor", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "contractor", label: "Contractor", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "landlord", label: "Landlord", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "tenant", label: "Tenant", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "caregiver", label: "Caregiver", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "professional caregiver", label: "Professional Caregiver", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "supervisee", label: "Supervisee", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "patient advocate", label: "Patient Advocate", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "caseworker", label: "Caseworker", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "social worker", label: "Social Worker", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "counselor", label: "Counselor", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "nurse", label: "Nurse", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "pharmacist", label: "Pharmacist", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "care coordinator", label: "Care Coordinator", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "peer support", label: "Peer Support", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "instructor", label: "Instructor", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "classmate", label: "Classmate", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "volunteer coordinator", label: "Volunteer Coordinator", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "teammate", label: "Teammate", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "sponsor", label: "Sponsor", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "nanny", label: "Nanny", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "girlfriend", label: "Girlfriend", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "employer", label: "Employer", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "boyfriend", label: "Boyfriend", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "babysitter", label: "Babysitter", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "foster parent", label: "Foster Parent", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "beneficiary", label: "Beneficiary", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "recruiter", label: "Recruiter", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "judge", label: "Judge", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "mediator", label: "Mediator", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "occupational therapist", label: "Occupational Therapist", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "physical therapist", label: "Physical Therapist", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "speech language pathologist", label: "Speech-Language Pathologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "respiratory therapist", label: "Respiratory Therapist", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "dietitian", label: "Dietitian", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "chiropractor", label: "Chiropractor", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "board certified behavioral analyst", label: "Board Certified Behavioral Analyst", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "massage therapist", label: "Massage Therapist", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "prosthetist", label: "Prosthetist", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "recreational therapist", label: "Recreational Therapist", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "art therapist", label: "Art Therapist", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "music therapist", label: "Music Therapist", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "cognitive behavioral therapist", label: "Cognitive Behavioral Therapist", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "dialectical behavior therapist", label: "Dialectical Behavior Therapist", get permissions() { return permission_suggestions[this.label] || []; } },
  { value: "tutor", label: "Tutor", get permissions() { return permission_suggestions[this.label] || []; } }
];
export const physicians = [
  { label: "Primary Care Physician", value: "primary care physician", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Cardiologist", value: "cardiologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Dentist", value: "dentist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Dermatologist", value: "dermatologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Endocrinologist", value: "endocrinologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Gastroenterologist", value: "gastroenterologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Hematologist", value: "hematologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Immunologist", value: "immunologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Nephrologist", value: "nephrologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Neurologist", value: "neurologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Obstetrician", value: "obstetrician", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Oncologist", value: "oncologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Ophthalmologist", value: "ophthalmologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Orthopedist", value: "orthopedist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Otolaryngologist", value: "otolaryngologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pediatrician", value: "pediatrician", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Psychiatrist", value: "psychiatrist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Therapist", value: "therapist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pulmonologist", value: "pulmonologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Radiologist", value: "radiologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Rheumatologist", value: "rheumatologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Urologist", value: "urologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Allergist", value: "allergist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Anesthesiologist", value: "anesthesiologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Cardiothoracic Surgeon", value: "cardiothoracic surgeon", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Colon and Rectal Surgeon", value: "colon and rectal surgeon", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Critical Care Specialist", value: "critical care specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Emergency Medicine Physician", value: "emergency medicine physician", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Family Medicine Physician", value: "family medicine physician", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Geriatrician", value: "geriatrician", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Gynecologist", value: "gynecologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Infectious Disease Specialist", value: "infectious disease specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Intensivist", value: "intensivist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Interventional Radiologist", value: "interventional radiologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Medical Geneticist", value: "medical geneticist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Neonatologist", value: "neonatologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Neurosurgeon", value: "neurosurgeon", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Occupational Medicine Physician", value: "occupational medicine physician", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pathologist", value: "pathologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Physiatrist", value: "physiatrist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Plastic Surgeon", value: "plastic surgeon", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Podiatrist", value: "podiatrist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Proctologist", value: "proctologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Sleep Medicine Specialist", value: "sleep medicine specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Sports Medicine Physician", value: "sports medicine physician", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Thoracic Surgeon", value: "thoracic surgeon", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Vascular Surgeon", value: "vascular surgeon", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Adolescent Medicine Specialist", value: "adolescent medicine specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Clinical Pharmacologist", value: "clinical pharmacologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Cytopathologist", value: "cytopathologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Developmental-Behavioral Pediatrician", value: "developmental-behavioral pediatrician", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Diabetologist", value: "diabetologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Emergency Medical Specialist", value: "emergency medical specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Hepatologist", value: "hepatologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Internal Medicine Specialist", value: "internal medicine specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Maxillofacial Surgeon", value: "maxillofacial surgeon", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Nuclear Medicine Specialist", value: "nuclear medicine specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pain Management Specialist", value: "pain management specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Sleep Specialist", value: "sleep specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Transplant Surgeon", value: "transplant surgeon", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Travel Medicine Specialist", value: "travel medicine specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Virologist", value: "virologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Bariatrician", value: "bariatrician", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Chemical Pathologist", value: "chemical pathologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Child and Adolescent Psychiatrist", value: "child and adolescent psychiatrist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Clinical Neurophysiologist", value: "clinical neurophysiologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Gynecologic Oncologist", value: "gynecologic oncologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Hand Surgeon", value: "hand surgeon", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Hospice and Palliative Medicine Specialist", value: "hospice and palliative medicine specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Infectious Disease Physician", value: "infectious disease physician", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Interventional Cardiologist", value: "interventional cardiologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Maternal-Fetal Medicine Specialist", value: "maternal-fetal medicine specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Molecular Geneticist", value: "molecular geneticist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Neurodevelopmental Disabilities Specialist", value: "neurodevelopmental disabilities specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Osteopathic Physician", value: "osteopathic physician", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pediatric Allergist/Immunologist", value: "pediatric allergist/immunologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pediatric Cardiologist", value: "pediatric cardiologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pediatric Critical Care Specialist", value: "pediatric critical care specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pediatric Emergency Medicine Specialist", value: "pediatric emergency medicine specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pediatric Endocrinologist", value: "pediatric endocrinologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pediatric Gastroenterologist", value: "pediatric gastroenterologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pediatric Hematologist-Oncologist", value: "pediatric hematologist-oncologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pediatric Infectious Disease Specialist", value: "pediatric infectious disease specialist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pediatric Nephrologist", value: "pediatric nephrologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pediatric Neurologist", value: "pediatric neurologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pediatric Pulmonologist", value: "pediatric pulmonologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pediatric Rheumatologist", value: "pediatric rheumatologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Pediatric Surgeon", value: "pediatric surgeon", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Reproductive Endocrinologist", value: "reproductive endocrinologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Cardiac Electrophysiologist", value: "cardiac electrophysiologist", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Dental Surgeon", value: "dental surgeon", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Gynecologic Surgeon", value: "gynecologic surgeon", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Ophthalmic Surgeon", value: "ophthalmic surgeon", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Orthopedic Surgeon", value: "orthopedic surgeon", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Trauma Surgeon", value: "trauma surgeon", get permissions() { return permission_suggestions[this.label] || []; } },
  { label: "Spinal Cord Injury Specialist", value: "spinal cord injury specialist", get permissions() { return permission_suggestions[this.label] || []; } }
];

export const states = [
  { value: "Alabama", label: "Alabama" },
  { value: "Alaska", label: "Alaska" },
  { value: "Arizona", label: "Arizona" },
  { value: "Arkansas", label: "Arkansas" },
  { value: "California", label: "California" },
  { value: "Colorado", label: "Colorado" },
  { value: "Connecticut", label: "Connecticut" },
  { value: "Delaware", label: "Delaware" },
  { value: "District Of Columbia", label: "District Of Columbia" },
  { value: "Florida", label: "Florida" },
  { value: "Georgia", label: "Georgia" },
  { value: "Hawaii", label: "Hawaii" },
  { value: "Idaho", label: "Idaho" },
  { value: "Illinois", label: "Illinois" },
  { value: "Indiana", label: "Indiana" },
  { value: "Iowa", label: "Iowa" },
  { value: "Kansas", label: "Kansas" },
  { value: "Kentucky", label: "Kentucky" },
  { value: "Louisiana", label: "Louisiana" },
  { value: "Maine", label: "Maine" },
  { value: "Maryland", label: "Maryland" },
  { value: "Massachusetts", label: "Massachusetts" },
  { value: "Michigan", label: "Michigan" },
  { value: "Minnesota", label: "Minnesota" },
  { value: "Mississippi", label: "Mississippi" },
  { value: "Missouri", label: "Missouri" },
  { value: "Montana", label: "Montana" },
  { value: "Nebraska", label: "Nebraska" },
  { value: "Nevada", label: "Nevada" },
  { value: "New Hampshire", label: "New Hampshire" },
  { value: "New Jersey", label: "New Jersey" },
  { value: "New Mexico", label: "New Mexico" },
  { value: "New York", label: "New York" },
  { value: "North Carolina", label: "North Carolina" },
  { value: "North Dakota", label: "North Dakota" },
  { value: "Ohio", label: "Ohio" },
  { value: "Oklahoma", label: "Oklahoma" },
  { value: "Oregon", label: "Oregon" },
  { value: "Pennsylvania", label: "Pennsylvania" },
  { value: "Rhode Island", label: "Rhode Island" },
  { value: "South Carolina", label: "South Carolina" },
  { value: "South Dakota", label: "South Dakota" },
  { value: "Tennessee", label: "Tennessee" },
  { value: "Texas", label: "Texas" },
  { value: "Utah", label: "Utah" },
  { value: "Vermont", label: "Vermont" },
  { value: "Virginia", label: "Virginia" },
  { value: "Washington", label: "Washington" },
  { value: "West Virginia", label: "West Virginia" },
  { value: "Wisconsin", label: "Wisconsin" },
  { value: "Wyoming", label: "Wyoming" }
];

export const units = [
  { label: 'Milligrams (mg)', value: 'mg' },
  { label: 'Micrograms (mcg)', value: 'mcg' },
  { label: 'Grams (g)', value: 'g' },
  { label: 'Milliliters (ml)', value: 'ml' },
  { label: 'MicroLiters (μl)', value: 'μl' },
  { label: 'Units', value: 'units' },
  { label: 'International Units (IU)', value: 'IU' },
  { label: 'Teaspoons (tsp)', value: 'tsp' },
  { label: 'Tablespoons (tbsp)', value: 'tbsp' },
  { label: 'Drops', value: 'drops' },
  { label: 'Puffs', value: 'puffs' },
  { label: 'Sprays', value: 'sprays' },
  { label: 'Pieces', value: 'pieces' },
  { label: 'Capsules', value: 'capsules' },
];

export const routes = [
  { label: 'Oral', value: 'oral' },
  { label: 'Sublingual', value: 'sublingual' },
  { label: 'Topical', value: 'topical' },
  { label: 'Transdermal', value: 'transdermal' },
  { label: 'Intravenous', value: 'intravenous' },
  { label: 'Intramuscular', value: 'intramuscular' },
  { label: 'Subcutaneous', value: 'subcutaneous' },
  { label: 'Inhalation', value: 'inhalation' },
  { label: 'Rectal', value: 'rectal' },
  { label: 'Vaginal', value: 'vaginal' },
  { label: 'Ophthalmic', value: 'ophthalmic' },
  { label: 'Otic', value: 'otic' },
  { label: 'Nasal', value: 'nasal' },
];

export const forms = [
  { label: 'Tablet', value: 'tablet' },
  { label: 'Capsule', value: 'capsule' },
  { label: 'Liquid', value: 'liquid' },
  { label: 'Solution', value: 'solution' },
  { label: 'Suspension', value: 'suspension' },
  { label: 'Drops', value: 'drops' },
  { label: 'Cream', value: 'cream' },
  { label: 'Ointment', value: 'ointment' },
  { label: 'Gel', value: 'gel' },
  { label: 'Patch', value: 'patch' },
  { label: 'Inhaler', value: 'inhaler' },
  { label: 'Injection', value: 'injection' },
  { label: 'Suppository', value: 'suppository' },
  { label: 'Lozenge', value: 'lozenge' },
  { label: 'Powder', value: 'powder' },
  { label: 'Granules', value: 'granules' },
  { label: 'Lotion', value: 'lotion' },
  { label: 'Spray', value: 'spray' },
];

/** Medication frequency options; includes all canonical values from backend so meds show in tabs/lists. */
export const frequencies = [
  { value: "daily", label: "Daily" },
  { value: "twice daily", label: "Twice daily" },
  { value: "three times daily", label: "Three times daily" },
  { value: "four times daily", label: "Four times daily" },
  { value: "five times daily", label: "Five times daily" },
  { value: "every 4 hours", label: "Every 4 hours" },
  { value: "every 6 hours", label: "Every 6 hours" },
  { value: "every 8 hours", label: "Every 8 hours" },
  { value: "every 12 hours", label: "Every 12 hours" },
  { value: "at bedtime", label: "At bedtime" },
  { value: "in the morning", label: "In the morning" },
  { value: "as needed", label: "As needed" },
  { value: "weekly", label: "Weekly" },
  { value: "twice weekly", label: "Twice weekly" },
  { value: "bi-weekly", label: "Bi-weekly" },
  { value: "every other week", label: "Every other week" },
  { value: "qod", label: "QOD (Every other day)" },
  { value: "every other day", label: "Every other day" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom" },
  { value: "other", label: "Other" }
];

export const custom_frequencies = [
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
  { value: "months", label: "Months" },
  { value: "years", label: "Years" }
];

export const pharmacies = [
  { label: 'Walgreens', value: 'walgreens' },
  { label: 'CVS Pharmacy', value: 'cvs' },
  { label: 'Rite Aid', value: 'rite aid' },
  { label: 'Walmart Pharmacy', value: 'walmart' },
  { label: 'Target (CVS)', value: 'target cvs' },
  { label: 'Kroger Pharmacy', value: 'kroger' },
  { label: 'Publix Pharmacy', value: 'publix' },
  { label: 'Costco Pharmacy', value: 'costco' },
  { label: 'Sam\'s Club Pharmacy', value: 'sams club' },
  { label: 'Health Mart', value: 'health mart' },
  { label: 'Dollar General', value: 'dollar general' },
  { label: 'H-E-B', value: 'h-e-b' },
  { label: 'Meijer Pharmacy', value: 'meijer' },
  { label: 'Kaiser Permanente', value: 'kaiser permanente' },
  { label: 'Fred Meyer', value: 'fred meyer' },
  { label: 'Giant Eagle', value: 'giant eagle' },
  { label: 'Ahold Delhaize', value: 'ahold delhaize' },
  { label: 'Safeway', value: 'safeway' },
  { label: 'Albertsons', value: 'albertsons' },
  { label: 'Raley\'s', value: 'raley\'s' },
  { label: 'Wegmans', value: 'wegmans' },
  { label: 'Hy-Vee', value: 'hy-vee' },
  { label: 'Duane Reade', value: 'duane reade' },
  { label: 'Bartell Drugs', value: 'bartell drugs' },
  { label: 'Good Neighbor Pharmacy', value: 'good neighbor' },
  { label: 'Medicine Shoppe', value: 'medicine shoppe' },
  { label: 'Kinney Drugs', value: 'kinney drugs' },
  { label: 'Acme Sav-on', value: 'acme sav-on' },
  { label: 'Cerberus', value: 'cerberus' },
  { label: 'Rx30', value: 'rx30' },
  { label: 'MedImpact', value: 'medimpact' },
  { label: 'OptumRx', value: 'optumrx' },
  { label: 'Express Scripts', value: 'express scripts' },
  { label: 'Aetna Rx Home Delivery', value: 'aetna rx home delivery' },
  { label: 'Humana', value: 'humana' },
  { label: 'Anthem Prescription Management', value: 'anthem prescription management' },
  { label: 'Caremark', value: 'caremark' },
  { label: 'Cigna Home Delivery', value: 'cigna home delivery' },
  { label: 'UnitedHealthcare', value: 'united healthcare' },
  { label: 'Kaiser Permanente Mail Order', value: 'kaiser permanente mail order' },
  { label: 'Navitus Health Solutions', value: 'navitus health solutions' },
  { label: 'Magellan Rx Management', value: 'magellan rx management' },
  { label: 'Catamaran Corporation', value: 'catamaran corporation' },
  { label: 'Prime Therapeutics', value: 'prime therapeutics' },
  { label: 'Maxor Specialty', value: 'maxor specialty' },
  { label: 'Diplomat Specialty', value: 'diplomat specialty' },
  { label: 'Curant Health', value: 'curant health' },
  { label: 'BioScrip', value: 'bioscrip' },
  { label: 'Option Care Health', value: 'option care health' },
  { label: 'Soleo Health', value: 'soleo health' },
  { label: 'Accredo', value: 'accredo' },
  { label: 'AllianceRx Walgreens Prime', value: 'alliancerx walgreens prime' },
  { label: 'Amber Pharmacy', value: 'amber pharmacy' },
  { label: 'CVS Specialty', value: 'cvs specialty' },
  { label: 'Diplomat Pharmacy', value: 'diplomat pharmacy' },
  { label: 'US Bioservices', value: 'us bioservices' }
];

export const side_effects = [
  { label: 'Abdominal pain', value: 'abdominal pain' },
  { label: 'Abnormal dreams', value: 'abnormal dreams' },
  { label: 'Abnormal taste', value: 'abnormal taste' },
  { label: 'Allergic reactions', value: 'allergic reactions' },
  { label: 'Anxiety', value: 'anxiety' },
  { label: 'Back pain', value: 'back pain' },
  { label: 'Blurred vision', value: 'blurred vision' },
  { label: 'Blood clots', value: 'blood clots' },
  { label: 'Chest pain', value: 'chest pain' },
  { label: 'Chest tightness', value: 'chest tightness' },
  { label: 'Chills', value: 'chills' },
  { label: 'Confusion', value: 'confusion' },
  { label: 'Constipation', value: 'constipation' },
  { label: 'Cough', value: 'cough' },
  { label: 'Depression', value: 'depression' },
  { label: 'Diarrhea', value: 'diarrhea' },
  { label: 'Difficulty concentrating', value: 'difficulty concentrating' },
  { label: 'Difficulty speaking', value: 'difficulty speaking' },
  { label: 'Difficulty swallowing', value: 'difficulty swallowing' },
  { label: 'Dizziness', value: 'dizziness' },
  { label: 'Dry mouth', value: 'dry mouth' },
  { label: 'Excessive thirst', value: 'excessive thirst' },
  { label: 'Fatigue', value: 'fatigue' },
  { label: 'Feeling hot', value: 'feeling hot' },
  { label: 'Fever', value: 'fever' },
  { label: 'Flatulence', value: 'flatulence' },
  { label: 'Flu-like symptoms', value: 'flu-like symptoms' },
  { label: 'Flushing', value: 'flushing' },
  { label: 'Gastrointestinal bleeding', value: 'gastrointestinal bleeding' },
  { label: 'Hallucinations', value: 'hallucinations' },
  { label: 'Headache', value: 'headache' },
  { label: 'Heart palpitations', value: 'heart palpitations' },
  { label: 'Hearing loss', value: 'hearing loss' },
  { label: 'Hot flashes', value: 'hot flashes' },
  { label: 'Hypertension', value: 'hypertension' },
  { label: 'Hypotension', value: 'hypotension' },
  { label: 'Increased appetite', value: 'increased appetite' },
  { label: 'Increased heart rate', value: 'increased heart rate' },
  { label: 'Increased sweating', value: 'increased sweating' },
  { label: 'Increased thirst', value: 'increased thirst' },
  { label: 'Increased urination', value: 'increased urination' },
  { label: 'Indigestion', value: 'indigestion' },
  { label: 'Insomnia', value: 'insomnia' },
  { label: 'Irritability', value: 'irritability' },
  { label: 'Itching', value: 'itching' },
  { label: 'Joint pain', value: 'joint pain' },
  { label: 'Kidney problems', value: 'kidney problems' },
  { label: 'Loss of appetite', value: 'loss of appetite' },
  { label: 'Malaise', value: 'malaise' },
  { label: 'Memory problems', value: 'memory problems' },
  { label: 'Migraines', value: 'migraines' },
  { label: 'Mood swings', value: 'mood swings' },
  { label: 'Muscle pain', value: 'muscle pain' },
  { label: 'Nasal congestion', value: 'nasal congestion' },
  { label: 'Nasal dryness', value: 'nasal dryness' },
  { label: 'Nausea', value: 'nausea' },
  { label: 'Nervousness', value: 'nervousness' },
  { label: 'Painful urination', value: 'painful urination' },
  { label: 'Palpitations', value: 'palpitations' },
  { label: 'Panic attacks', value: 'panic attacks' },
  { label: 'Peripheral edema', value: 'peripheral edema' },
  { label: 'Photosensitivity', value: 'photosensitivity' },
  { label: 'Rash', value: 'rash' },
  { label: 'Restlessness', value: 'restlessness' },
  { label: 'Runny nose', value: 'runny nose' },
  { label: 'Seizures', value: 'seizures' },
  { label: 'Sexual dysfunction', value: 'sexual dysfunction' },
  { label: 'Shortness of breath', value: 'shortness of breath' },
  { label: 'Sneezing', value: 'sneezing' },
  { label: 'Sore throat', value: 'sore throat' },
  { label: 'Speech difficulties', value: 'speech difficulties' },
  { label: 'Stomach cramps', value: 'stomach cramps' },
  { label: 'Sweating', value: 'sweating' },
  { label: 'Swelling', value: 'swelling' },
  { label: 'Taste disturbances', value: 'taste disturbances' },
  { label: 'Tingling sensations', value: 'tingling sensations' },
  { label: 'Tinnitus', value: 'tinnitus' },
  { label: 'Tiredness', value: 'tiredness' },
  { label: 'Urinary retention', value: 'urinary retention' },
  { label: 'Vision changes', value: 'vision changes' },
  { label: 'Watery eyes', value: 'watery eyes' },
  { label: 'Weight gain', value: 'weight gain' },
  { label: 'Weight loss', value: 'weight loss' },
  { label: 'Wind', value: 'wind' },
  { label: 'Withdrawal symptoms', value: 'withdrawal symptoms' },
];

export const fetchDiagnoses = async (term) => {
  const baseUrl = 'https://clinicaltables.nlm.nih.gov/api/conditions/v3/search';
  const parameters = {
    terms: term,
    df: 'key_id,info_link_data,icd10cm_codes,icd10cm,consumer_name,primary_name,word_synonyms,synonyms,term_icd9_code,term_icd9_text'
  };
  try {
    const url = new URL(baseUrl);
    url.search = new URLSearchParams(parameters).toString();
    const response = await fetch(url);
    const data = await response.json();
    const normalizedData = [];
    data[3].forEach((item) => {
      if (Array.isArray(item)) {
        normalizedData.push({
          id: item[0],
          link: item[1]?.split(',')[0],
          consumerName: item[4],
          primaryName: item[5],
          synonyms: item[6]?.split(";"),
          other_name: item[7],
          icd10: item[2]
        });
      }
    });
    return normalizedData.map((e) => e.primaryName || e.consumerName || e.other_name);
  } catch (error) {
    debug.error('Error extracting names from data', error);
    return [];
  }
};

export const convertGigabytesToBytes = (gigabytes) => {
  const bytesInAGigabyte = 1024 * 1024 * 1024;
  const bytes = gigabytes * bytesInAGigabyte;
  return Math.round(bytes); // Round the result to the nearest integer
};

export const convertBytesToGigabytes = (bytes) => {
  const bytesInAGigabyte = 1024 * 1024 * 1024;
  const gigabytes = bytes / bytesInAGigabyte;
  return Math.round(gigabytes);
};

export const trackEvent = (name, data, action, category) => {
  if (!process.env.REACT_APP_LOCAL) LogRocket.track(name, data);
  if (name && action && category && process.env.REACT_APP_STAGE === 'production') {
    ReactGA.event({
      category,
      action,
      label: name
    });
  }
};

export const uploadToImgur = async (image, account_id) => {
  let myHeaders = new Headers();
  myHeaders.append("Authorization", "Client-ID a0b667f24a45ad8");

  let formdata = new FormData();
  formdata.append("image", image.data.replace(/^data:image\/(png|jpg);base64,/, ""));
  formdata.append("name", `${account_id}_${image.name}`);
  formdata.append("type", "base64");

  const requestOptions = {
    method: "POST",
    headers: myHeaders,
    body: formdata,
    redirect: "follow"
  };

  return fetch("https://api.imgur.com/3/image", requestOptions)
    .then(response => response.json())
    .then(result => result.data.link)
    .catch(error => debug.error('Short link generation error', error));
};
export const notifyToSlackChannel = async (
  webHookURL,
  message,
  images = [],
  account_id,
  channel,
  userName,
  botIconURL
) => {
  if (webHookURL && webHookURL.trim() && message && message.trim()) {
    let payload = {
      blocks: [
        {
          "type": "section",
          "text": {
            "type": "mrkdwn",
            "text": message
          }
        }
      ]
    };
    if (channel && channel.trim()) {
      payload.channel = channel
    }
    if (userName && userName.trim()) {
      payload.username = userName
    }
    if (botIconURL && botIconURL.trim()) {
      payload.icon_url = botIconURL
    }
    if (images.length) {
      for (let i = 0; i < images.length; i++) {
        const url = await uploadToImgur(images[i], account_id);
        if (url) {
          payload.blocks.push({
            type: "image",
            title: {
              type: "plain_text",
              text: images[i].name
            },
            block_id: `${account_id}_${images[i].name}`,
            image_url: url,
            alt_text: `${account_id}_${images[i].name}`
          });
        }
      }
    }

    // eslint-disable-next-line no-undef
    return fetch(webHookURL, {
      method: "POST",
      body: JSON.stringify(payload)
    })
      .then((response) => response)
      .then((response) => response)
      .catch((error) => error)
  }
  return null
};

export const getRatingFaces = () => {
  const AngryFace = (
    <path d="M256 32a224 224 0 1 1 0 448 224 224 0 1 1 0-448zm0 480A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm82.7-116.1c6.6-5.9 7.1-16 1.2-22.6C323.8 355.4 295.7 336 256 336s-67.8 19.4-83.9 37.3c-5.9 6.6-5.4 16.7 1.2 22.6s16.7 5.4 22.6-1.2c11.7-13 31.6-26.7 60.1-26.7s48.4 13.7 60.1 26.7c5.9 6.6 16 7.1 22.6 1.2zM176.4 264c13.3 0 24-10.7 24-24c0-2.6-.4-5.1-1.2-7.4l19.7 6.6c8.4 2.8 17.4-1.7 20.2-10.1s-1.7-17.4-10.1-20.2l-96-32c-8.4-2.8-17.4 1.7-20.2 10.1s1.7 17.4 10.1 20.2l39.6 13.2c-6.2 4.3-10.2 11.5-10.2 19.6c0 13.3 10.7 24 24 24zm184-24c0-8.2-4.1-15.5-10.4-19.8l39.1-13c8.4-2.8 12.9-11.9 10.1-20.2s-11.9-12.9-20.2-10.1l-96 32c-8.4 2.8-12.9 11.9-10.1 20.2s11.9 12.9 20.2 10.1l20.6-6.9c-.8 2.4-1.3 5-1.3 7.7c0 13.3 10.7 24 24 24s24-10.7 24-24z" strokeWidth="9px" />
  );


  const SadFace = (
    <path d="M12.0000002,1.99896738 C17.523704,1.99896738 22.0015507,6.47681407 22.0015507,12.0005179 C22.0015507,17.5242217 17.523704,22.0020684 12.0000002,22.0020684 C6.47629639,22.0020684 1.99844971,17.5242217 1.99844971,12.0005179 C1.99844971,6.47681407 6.47629639,1.99896738 12.0000002,1.99896738 Z M12.0000002,3.49896738 C7.30472352,3.49896738 3.49844971,7.30524119 3.49844971,12.0005179 C3.49844971,16.6957946 7.30472352,20.5020684 12.0000002,20.5020684 C16.6952769,20.5020684 20.5015507,16.6957946 20.5015507,12.0005179 C20.5015507,7.30524119 16.6952769,3.49896738 12.0000002,3.49896738 Z M12.0000001,13.4979816 C13.6312483,13.4979816 15.1603686,14.1528953 16.2810488,15.2934358 C16.5713583,15.5888901 16.5671876,16.0637455 16.2717333,16.354055 C15.976279,16.6443646 15.5014236,16.6401939 15.211114,16.3447396 C14.3696444,15.4883577 13.2246935,14.9979816 12.0000001,14.9979816 C10.7726114,14.9979816 9.62535029,15.4905359 8.78347552,16.3502555 C8.49366985,16.6462041 8.01882223,16.6511839 7.72287367,16.3613782 C7.4269251,16.0715726 7.4219453,15.5967249 7.71175097,15.3007764 C8.83296242,14.155799 10.3651558,13.4979816 12.0000001,13.4979816 Z M9.00044779,8.75115873 C9.69041108,8.75115873 10.2497368,9.3104845 10.2497368,10.0004478 C10.2497368,10.6904111 9.69041108,11.2497368 9.00044779,11.2497368 C8.3104845,11.2497368 7.75115873,10.6904111 7.75115873,10.0004478 C7.75115873,9.3104845 8.3104845,8.75115873 9.00044779,8.75115873 Z M15.0004478,8.75115873 C15.6904111,8.75115873 16.2497368,9.3104845 16.2497368,10.0004478 C16.2497368,10.6904111 15.6904111,11.2497368 15.0004478,11.2497368 C14.3104845,11.2497368 13.7511587,10.6904111 13.7511587,10.0004478 C13.7511587,9.3104845 14.3104845,8.75115873 15.0004478,8.75115873 Z" />
  );

  const NeutralFace = (
    <div>
      <path d="M9.00051 8.75122C9.69047 8.75122 10.2498 9.31055 10.2498 10.0005C10.2498 10.6905 9.69047 11.2498 9.00051 11.2498C8.31055 11.2498 7.75122 10.6905 7.75122 10.0005C7.75122 9.31055 8.31055 8.75122 9.00051 8.75122Z" />
      <path d="M15.0005 8.75122C15.6905 8.75122 16.2498 9.31055 16.2498 10.0005C16.2498 10.6905 15.6905 11.2498 15.0005 11.2498C14.3105 11.2498 13.7512 10.6905 13.7512 10.0005C13.7512 9.31055 14.3105 8.75122 15.0005 8.75122Z" />
      <path d="M8.25 15C7.83579 15 7.5 15.3358 7.5 15.75C7.5 16.1642 7.83579 16.5 8.25 16.5H15.75C16.1642 16.5 16.5 16.1642 16.5 15.75C16.5 15.3358 16.1642 15 15.75 15H8.25Z" />
      <path d="M2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM12 3.5C7.30558 3.5 3.5 7.30558 3.5 12C3.5 16.6944 7.30558 20.5 12 20.5C16.6944 20.5 20.5 16.6944 20.5 12C20.5 7.30558 16.6944 3.5 12 3.5Z" />
    </div>
  );

  const SmilingFace = (
    <path d="M12.0000002,1.99896738 C17.523704,1.99896738 22.0015507,6.47681407 22.0015507,12.0005179 C22.0015507,17.5242217 17.523704,22.0020684 12.0000002,22.0020684 C6.47629639,22.0020684 1.99844971,17.5242217 1.99844971,12.0005179 C1.99844971,6.47681407 6.47629639,1.99896738 12.0000002,1.99896738 Z M12.0000002,3.49896738 C7.30472352,3.49896738 3.49844971,7.30524119 3.49844971,12.0005179 C3.49844971,16.6957946 7.30472352,20.5020684 12.0000002,20.5020684 C16.6952769,20.5020684 20.5015507,16.6957946 20.5015507,12.0005179 C20.5015507,7.30524119 16.6952769,3.49896738 12.0000002,3.49896738 Z M8.46174078,14.7838355 C9.31087697,15.8615555 10.6018926,16.5020843 11.9999849,16.5020843 C13.396209,16.5020843 14.6856803,15.8632816 15.5349376,14.7880078 C15.7916692,14.4629512 16.2633016,14.4075628 16.5883582,14.6642944 C16.9134148,14.9210259 16.9688032,15.3926584 16.7120717,15.717715 C15.5813083,17.1494133 13.8601276,18.0020843 11.9999849,18.0020843 C10.1373487,18.0020843 8.41411759,17.1471146 7.28351576,15.7121597 C7.02716611,15.3868018 7.08310832,14.9152347 7.40846617,14.6588851 C7.73382403,14.4025354 8.20539113,14.4584777 8.46174078,14.7838355 Z M9.00044779,8.75115873 C9.69041108,8.75115873 10.2497368,9.3104845 10.2497368,10.0004478 C10.2497368,10.6904111 9.69041108,11.2497368 9.00044779,11.2497368 C8.3104845,11.2497368 7.75115873,10.6904111 7.75115873,10.0004478 C7.75115873,9.3104845 8.3104845,8.75115873 9.00044779,8.75115873 Z M15.0004478,8.75115873 C15.6904111,8.75115873 16.2497368,9.3104845 16.2497368,10.0004478 C16.2497368,10.6904111 15.6904111,11.2497368 15.0004478,11.2497368 C14.3104845,11.2497368 13.7511587,10.6904111 13.7511587,10.0004478 C13.7511587,9.3104845 14.3104845,8.75115873 15.0004478,8.75115873 Z" />
  );

  const HappyFace = (
    <div>
      <path d="M6.74927 12C6.53852 12 6.33749 12.0887 6.19539 12.2443C6.05329 12.4 5.98323 12.6082 6.00237 12.8181C6.28259 15.8916 8.55224 18.5 12 18.5C15.4478 18.5 17.7174 15.8916 17.9977 12.8181C18.0168 12.6082 17.9468 12.4 17.8047 12.2443C17.6626 12.0887 17.4616 12 17.2508 12H6.74927ZM12 17C9.74286 17 8.12852 15.5205 7.63237 13.5H16.3677C15.8715 15.5205 14.2571 17 12 17Z" />
      <path d="M15.2501 8.75C14.8416 8.75 14.5398 9.03719 14.492 9.35982C14.4314 9.76957 14.05 10.0526 13.6403 9.99192C13.2305 9.93126 12.9475 9.54993 13.0082 9.14018C13.1696 8.0495 14.1313 7.25 15.2501 7.25C16.3689 7.25 17.3306 8.0495 17.492 9.14018C17.5527 9.54993 17.2697 9.93126 16.8599 9.99192C16.4502 10.0526 16.0688 9.76957 16.0082 9.35982C15.9604 9.03719 15.6586 8.75 15.2501 8.75Z" />
      <path d="M7.99202 9.35982C8.03977 9.03719 8.34157 8.75 8.7501 8.75C9.15863 8.75 9.46043 9.03719 9.50818 9.35982C9.56884 9.76957 9.95017 10.0526 10.3599 9.99192C10.7697 9.93126 11.0527 9.54993 10.992 9.14018C10.8306 8.0495 9.86892 7.25 8.7501 7.25C7.63128 7.25 6.66963 8.0495 6.50818 9.14018C6.44753 9.54993 6.73053 9.93126 7.14028 9.99192C7.55003 10.0526 7.93136 9.76957 7.99202 9.35982Z" />
      <path d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM3.5 12C3.5 7.30558 7.30558 3.5 12 3.5C16.6944 3.5 20.5 7.30558 20.5 12C20.5 16.6944 16.6944 20.5 12 20.5C7.30558 20.5 3.5 16.6944 3.5 12Z" />
    </div>
  );

  const customStyles = {
    itemShapes: [AngryFace, SadFace, NeutralFace, SmilingFace, HappyFace],
    activeFillColor: ["#da1600", "#dcb000", "#ee6c4c", "#61bb00", "#009664"],
    inactiveFillColor: "#a8a8a8",
  };
  return customStyles;
};

export const generateCredentials = async (user) => {
  try {
    if (!user?.cognito_id) return false;
    const response = await getAuthChallenge();
    const challenge = response?.payload?.challenge || response?.payload;
    if (!challenge) return false;
    const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'User';
    let credential;
    try {
      credential = await client.register({
        challenge,
        user: displayName,
        userVerification: 'required',
        attestation: 'none',
        timeout: 60000
      });
    } catch (e) {
      debug.error('Passkey registration failed', e);
      return false;
    }
    if (!credential) return false;
    const registered = await storeCredential(credential, challenge);
    if (registered.success) {
      await getUser(user.cognito_id, store.getState().session.account.account_id, true);
      return true;
    }
    return false;
  } catch (error) {
    debug.error('Error generating passkey credentials', error);
    return false;
  }
};

export const passkeyAuth = async () => {
  if (window.__PASSKEY_AUTH_IN_FLIGHT) {
    return false; // avoid parallel prompts
  }
  window.__PASSKEY_AUTH_IN_FLIGHT = true;
  const origin = window.location.origin;
  const state = store.getState();
  const passkeys = state.authentication?.user?.passkeys || [];
  const actives = passkeys.filter(p => p?.origin === origin && p?.id);
  if (!actives.length) { window.__PASSKEY_AUTH_IN_FLIGHT = false; return true; } // nothing to enforce
  // lock immediately
  // Only show lock if not already explicitly hidden
  if (store.getState().session.locked?.show !== false) {
    store.dispatch({ type: SET_LOCKED, payload: { show: true, inactive: false } });
  }
  let challengeResp;
  try { challengeResp = await getAuthChallenge(true); } catch (e) { 
    debug.error('Passkey challenge fetch failed', e); 
    window.__PASSKEY_AUTH_IN_FLIGHT = false; 
    return false; 
  }
  const challenge = challengeResp?.payload?.challenge || challengeResp?.payload;
  if (!challenge) { window.__PASSKEY_AUTH_IN_FLIGHT = false; return false; }
  const ids = actives.map(p => p.id);
  // Library regression handling: pass plain ID strings first (was failing when given ArrayBuffer descriptors)
  let allowCredentials = ids.slice();
  let authentication;
  try {
    authentication = await client.authenticate({ challenge, allowCredentials, userVerification:'required', timeout:60000 });
  } catch (e) {
    if (e?.name === 'NotAllowedError') { window.__PASSKEY_AUTH_IN_FLIGHT = false; return false; } // cancel
    debug.warn('[passkey] authenticate error with id list, retrying with descriptors', e);
    // Fallback to descriptor objects if plain IDs failed for unexpected reason
    try {
      const b64urlToBuf = (b64) => { try { const pad = '='.repeat((4-(b64.length%4))%4); const base64 = b64.replace(/-/g,'+').replace(/_/g,'/')+pad; const raw=atob(base64); const arr=new Uint8Array(raw.length); for(let i=0;i<raw.length;i++) arr[i]=raw.charCodeAt(i); return arr.buffer; } catch { return null; } };
      allowCredentials = ids.map(id => ({ type:'public-key', id: b64urlToBuf(id) })).filter(d=>d.id);
      if (!allowCredentials.length) { window.__PASSKEY_AUTH_IN_FLIGHT = false; return false; }
      authentication = await client.authenticate({ challenge, allowCredentials, userVerification:'required', timeout:60000 });
    } catch (e2) {
      debug.error('[passkey] authenticate error (fallback descriptors)', e2); window.__PASSKEY_AUTH_IN_FLIGHT = false; return false;
    }
  }
  if (!authentication) { window.__PASSKEY_AUTH_IN_FLIGHT = false; return false; }
  const returnedId = authentication.id || authentication.credentialId || authentication.rawId || authentication?.credential?.id;
  if (!returnedId) { debug.error('[passkey] missing returned credential id'); window.__PASSKEY_AUTH_IN_FLIGHT = false; return false; }
  // Don't block solely on ids mismatch; some libs may transform id (e.g., base64url vs raw). Server will validate.
  let verifyRes;
  // Normalize credential payload: ensure credentialId field and base64url encoding of binary buffers
  const toB64Url = (buf) => {
    try {
      if (!buf) return undefined;
      if (typeof buf === 'string') return buf; // already string
      let bytes;
      if (buf instanceof ArrayBuffer) bytes = new Uint8Array(buf);
      else if (ArrayBuffer.isView(buf)) bytes = new Uint8Array(buf.buffer);
      else return undefined;
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      const base64 = btoa(binary).replace(/=+$/,'').replace(/\+/g,'-').replace(/\//g,'_');
      return base64;
    } catch { return undefined; }
  };
  const normalized = { ...authentication };
  // Some libs give rawId as base64url already; ensure credentialId present
  if (!normalized.credentialId) normalized.credentialId = normalized.id || normalized.rawId;
  // Convert rawId if it's ArrayBuffer
  if (normalized.rawId instanceof ArrayBuffer) normalized.rawId = toB64Url(normalized.rawId);
  // Attach transformed representation expected by backend query (credential->>'id' matches credentialId string stored)
  if (normalized.credentialId instanceof ArrayBuffer) normalized.credentialId = toB64Url(normalized.credentialId);
  // Convert nested response ArrayBuffers (authenticatorData, clientDataJSON, signature, userHandle)
  if (normalized.response) {
    const r = { ...normalized.response };
    ['authenticatorData','clientDataJSON','signature','userHandle'].forEach(k => {
      if (r[k]) r[k] = toB64Url(r[k]);
    });
    normalized.response = r;
  }
  try { verifyRes = await verifyCredential(normalized, challenge); } catch (e) { debug.error('[passkey] verify error', e); window.__PASSKEY_AUTH_IN_FLIGHT = false; return false; }
  if (!verifyRes?.success) { debug.error('[passkey] server verify rejected'); window.__PASSKEY_AUTH_IN_FLIGHT = false; return false; }
  store.dispatch({ type: SET_LOCKED, payload: { show: false, inactive: false } });
  window.__PASSKEY_AUTH_IN_FLIGHT = false;
  return true;
};

export const checkSmartScheduleDays = (questionObjects, timespanInDays) => {
  const sortedQuestions = sortQuestionsByCreationTime(questionObjects);
  const startDate = calculateStartDate(timespanInDays);
  const questionsPerDayMap = new Map();
  sortedQuestions.forEach((question) => {
    const questionDate = moment.utc(question.created_at);
    if (questionDate.isSameOrAfter(startDate, "day")) {
      const formattedDate = questionDate.format("YYYY-MM-DD");
      questionsPerDayMap.set(formattedDate, (questionsPerDayMap.get(formattedDate) || 0) + 1);
    }
  });
  return questionsPerDayMap;
};

export const calculateAverageHour = (objectsArray = []) => {
  if (!objectsArray.length) return null;

  // Calculate the total hours
  const totalHours = objectsArray.reduce((sum, obj) => {
    if (obj && obj.created_at) {
      const date = new Date(obj.created_at);
      return sum + date.getHours();
    }
    return sum;
  }, 0);

  // Calculate the average hour
  const averageHour = Math.round(totalHours / objectsArray.length);

  return averageHour;
};

export const adaptPromptCadence = (questionObjects, timespanInDays, currentCadence, currentTime, cutOffTime) => {
  try {
    const MIN_HOURS_BEFORE_CUTOFF = 5;
    const MAX_PROMPTS_PER_DAY = 4;
    if (isCutoffTimeImminent(currentTime, cutOffTime, MIN_HOURS_BEFORE_CUTOFF)) return 1;

    const questionsPerDayMap = checkSmartScheduleDays(questionObjects, timespanInDays);

    if (isBelowMinimumSampleSize(questionsPerDayMap, SMART_SCHEDULE_THRESHOLD)) return 1;

    const topValues = getTopQuestionCounts(questionsPerDayMap);
    const averageTopValues = calculateAverage(topValues);

    const adaptedCadence = calculateAdaptedCadence(averageTopValues, currentCadence, MAX_PROMPTS_PER_DAY);
    return adaptedCadence;
  } catch (error) {
    debug.error('Error in adaptPromptCadence', error.message);
    return 1;
  }
};

// Helper functions

const isCutoffTimeImminent = (currentTime, cutOffTime, minHoursBeforeCutoff) => {
  return (cutOffTime - currentTime) < minHoursBeforeCutoff;
};

const sortQuestionsByCreationTime = (questionObjects) => {
  return questionObjects.slice().sort((a, b) => moment.utc(a.created_at) - moment.utc(b.created_at));
};

const calculateStartDate = (timespanInDays) => {
  return moment.utc().subtract(timespanInDays, "days");
};

export const isBelowMinimumSampleSize = (questionsPerDayMap, minSampleSize) => {
  return questionsPerDayMap.size < minSampleSize;
};

const getTopQuestionCounts = (questionsPerDayMap) => {
  return Array.from(questionsPerDayMap.values()).sort((a, b) => b - a);
};

const calculateAverage = (values) => {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const calculateAdaptedCadence = (averageTopValues, currentCadence, maxPromptsPerDay) => {
  const adaptedCadence = Math.max(1, Math.min(maxPromptsPerDay, Math.round(averageTopValues)));
  return (currentCadence > 1) ? Math.min(currentCadence, adaptedCadence) : adaptedCadence;
};

export const getMaxLimit = (name, metadata = {}, subscription = {}, user_plan = {}, whitelabel = {}) => {
  return Math.max(
    parseInt((metadata?.[name])) || 0,
      (subscription?.[name] || 0),
      (user_plan?.[name] || 0),
      (whitelabel?.[name] || 0)
  )
};

export const generateQuestionTimes = (adaptedCadence, notificationTime, cutoffTime) => {
  // Parse the notification and cutoff times
  const parsedNotificationTime = moment(notificationTime, "HH");
  const parsedCutoffTime = moment(cutoffTime, "HH");

  // Start with the first question time as the notification time
  let questionTimes = [parsedNotificationTime.hour()];

  // If adaptedCadence is 1, return the initial question time
  if (adaptedCadence === 1) return questionTimes;

  // Calculate the available hours for questions
  const availableHours = parsedCutoffTime.diff(parsedNotificationTime, "hours");

  // Calculate the time interval between questions
  const timeInterval = Math.floor(availableHours / (adaptedCadence));

  // Generate the next question times until reaching adaptedCadence or exceeding the cutoff time
  for (let i = 1; i < adaptedCadence; i++) {
    let nextQuestionTime = parsedNotificationTime.clone().add(i * timeInterval, "hours");

    // If the next question time is after the cutoff time, break the loop
    if (nextQuestionTime.isSameOrAfter(parsedCutoffTime)) break;

    // Add the next question time to the array
    if (!questionTimes.includes(nextQuestionTime.hour())) questionTimes.push(nextQuestionTime.hour());
  }

  // Check if the last question time is too close to the cutoffTime
  const lastQuestionTime = moment(questionTimes[questionTimes.length - 1], "HH");
  const timeDifference = parsedCutoffTime.diff(lastQuestionTime, "hours");

  // If the last question time is too close, remove it
  if (timeDifference < timeInterval) questionTimes.pop();

  return questionTimes.sort((a, b) => a - b); // Sort the array in ascending order
};

export const clearLocalStorageItems = (prefix) => {
  Object.keys(localStorage)
    .filter((x) => x.startsWith(prefix)).forEach((x) => localStorage.removeItem(x))
};

export const rgbaToHex = (rgbaString) => {
  const rgbaValues = rgbaString.match(/\d+(\.\d+)?/g);
  const r = parseInt(rgbaValues[0]);
  const g = parseInt(rgbaValues[1]);
  const b = parseInt(rgbaValues[2]);
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

export const calculateBMI = (heightFeet, heightInches, weightPounds) => {
  let totalInches = (heightFeet * 12) + heightInches;
  let bmi = (weightPounds / (totalInches * totalInches)) * 703;
  return bmi.toFixed(2);
};

export const calculateBMR = (weight, feet, inches, age, gender) =>{
  let bmr = 0;
  const totalHeightInInches = feet * 12 + inches;
  if (gender.toLowerCase() === 'male') {
    bmr = 66 + (6.23 * weight) + (12.7 * totalHeightInInches) - (6.8 * age);
  } else if (gender.toLowerCase() === 'female') {
    bmr = 655 + (4.35 * weight) + (4.7 * totalHeightInInches) - (4.7 * age);
  } else {
    throw new Error('Invalid gender. Please specify "male" or "female".');
  }
  return Math.round(bmr);
};

export const convertTimestampsToReadable = (text) => {
  // Regex to match 24-hour time: HH:MM (00:00 to 23:59)
  const time24Regex = /\b([01]\d|2[0-3]):([0-5]\d)\b/g;

  // Regex to match ISO 8601 timestamps with time (e.g. 2025-06-25T17:56:00Z)
  const isoDateTimeRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?/g;

  // Regex to match ISO dates without time (e.g. 2005-06-22)
  // Make sure it does NOT match longer datetime by negative lookahead
  const isoDateOnlyRegex = /\b\d{4}-\d{2}-\d{2}\b(?!T)/g;

  // Regex to match Unix timestamps (seconds since epoch)
  const unixRegex = /\b(1[5-9]\d{8,9}|2\d{9,10})\b/g;

  // Regex to match 'Mon Nov 18 2024 23:36:58 GMT+0000' style timestamps
  const longDateRegex = /\b(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun) [A-Z][a-z]{2} \d{1,2} \d{4} \d{2}:\d{2}:\d{2} GMT[+-]\d{4}\b/g;

  const formatterDateTime = new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });

  const formatterDateOnly = new Intl.DateTimeFormat('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  // Replace ISO datetime timestamps
  text = text.replace(isoDateTimeRegex, (match) => {
    const date = new Date(match);
    if (isNaN(date)) return match;
    return formatterDateTime.format(date);
  });

  // Replace ISO dates without time
  text = text.replace(isoDateOnlyRegex, (match) => {
    const date = new Date(match);
    if (isNaN(date)) return match;
    return formatterDateOnly.format(date);
  });

  // Replace Unix timestamps
  text = text.replace(unixRegex, (match) => {
    const date = new Date(Number(match) * 1000);
    if (isNaN(date)) return match;
    return formatterDateTime.format(date);
  });

  // Replace long format dates
  text = text.replace(longDateRegex, (match) => {
    const date = new Date(match);
    if (isNaN(date)) return match;
    return formatterDateTime.format(date);
  });

  text = text.replace(time24Regex, (match, hourStr, minuteStr) => {
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    if (hour === 0) hour = 12; // midnight or noon is 12
    const time = `${hour}:${minute}`;
    return !time.includes("AM") && !time.includes("PM") ? time : `${time} ${ampm}`;
  });

  return text;
};
export const markdownToHtml = (markdown, options = {}) => {
  const streaming = options.streaming === true;
  // Tables FIRST - process before anything else can interfere with pipe characters
  // When streaming: only convert when we have at least one complete body row so partial table doesn't flash
  markdown = markdown.replace(
    /^[ \t]*(\|[^\n]+\|)[ \t]*\n[ \t]*(\|[-:| ]+\|)[ \t]*(?:\n((?:[ \t]*\|[^\n]*\|[ \t]*\n?)*))?/gm,
    (match, headerLine, separatorLine, bodyLines = '') => {
      const body = (bodyLines || '').trim();
      const hasCompleteRow = body && body.split('\n').some(line => line.trim() && line.includes('|'));
      if (streaming && !hasCompleteRow) {
        return '<div class="streaming-table-placeholder" aria-busy="true" style="padding:12px 16px;border:1px dashed rgba(0,0,0,0.15);border-radius:8px;background:rgba(0,0,0,0.03);color:#666;font-size:14px;">Loading table…</div>';
      }
      // Process cell content - allow specific HTML tags and format phone numbers
      const processCellContent = (str = '') => {
        if (!str) return '';
        
        // Format phone numbers (match +1XXXXXXXXXX or plain 10-digit numbers)
        str = str.replace(/\+1(\d{10})/g, (match, digits) => {
          return formatUSPhoneNumberPretty(digits);
        });
        
        // Escape HTML but preserve specific allowed tags
        let processed = str
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        
        // Un-escape allowed tags - handles both simple tags and tags with attributes
        processed = processed
          .replace(/&lt;(a)\s+([^&]*?)&gt;/gi, '<$1 $2>')
          .replace(/&lt;\/(a)&gt;/gi, '</$1>');
        
        processed = processed
          .replace(/&lt;(\/?)(strong|em|u|s|code|mark|br|ul|ol|li|p|hr)(\s*\/?)&gt;/gi, '<$1$2$3>');
        
        return processed;
      };
      
      // Parse header cells - split by pipe and remove first/last empty elements
      const headerCells = headerLine.trim().split('|').filter(Boolean);
      const numColumns = headerCells.length;
      
      // Parse alignment from separator line
      const alignmentCells = separatorLine.trim().split('|').filter(Boolean);
      const alignments = alignmentCells.map((cell) => {
        const t = cell.trim();
        if (/^:-+:$/.test(t)) return 'center';
        if (/^:-+/.test(t)) return 'left';
        if (/-+:$/.test(t)) return 'right';
        return 'left';
      });
      
      // Build header HTML
      const headers = headerCells
        .map((h, i) => `<th style="text-align:${alignments[i] || 'left'}">${processCellContent(h.trim())}</th>`)
        .join('');
      
      // Parse body rows
      let rows = '';
      
      if (body) {
        const rowLines = body.split('\n').filter(line => line.trim() && line.includes('|'));
        rows = rowLines
          .map(row => {
            // Split row by pipe, filter out empty leading/trailing
            const rawCells = row.trim().split('|').filter(Boolean);
            
            // Ensure we have exactly numColumns cells
            const cells = [];
            for (let i = 0; i < numColumns; i++) {
              cells.push(rawCells[i] !== undefined ? rawCells[i] : '');
            }
            
            return `<tr>${cells.map((c, i) => `<td style="text-align:${alignments[i] || 'left'}">${processCellContent(c.trim())}</td>`).join('')}</tr>`;
          })
          .join('');
      }
      
      // Wrap table in a scrollable div
      return `<div style="overflow-x: auto; max-width: 100%; -webkit-overflow-scrolling: touch;"><table><thead><tr>${headers}</tr></thead>${rows ? `<tbody>${rows}</tbody>` : ''}</table></div>`;
    }
  );

  // Horizontal rules: --- *** ___ (but not table separators which have pipes)
  markdown = markdown.replace(/^[-*_]{3,}$/gm, "<hr>");

  // Code blocks: ```lang\ncode\n```
  markdown = markdown.replace(/```(?:\w*\n)?([\s\S]*?)```/g, (_, code) => {
    return `<pre><code>${code.trim()}</code></pre>`;
  });

  // Inline code: `code`
  markdown = markdown.replace(/`([^`]+)`/g, "<code>$1</code>");

  // Blockquotes: > quote
  markdown = markdown.replace(/^> (.*$)/gm, "<blockquote>$1</blockquote>");

  // Headers: #, ##, ###, ####
  markdown = markdown.replace(/^#{1,6} (.*)$/gm, (_, content) => {
    const level = _.match(/^#+/)[0].length;
    return `<h${level}>${content}</h${level}>`;
  });

  // Bold: **text**
  markdown = markdown.replace(/\*\*(.*?)\*\*/g, (_, content) => {
    if (content.endsWith(":")) return `<strong class='strong-title'>${content}</strong>`;
    return `<strong>${content}</strong>`;
  });

  // Italic: *text*
  markdown = markdown.replace(/(?<!\*)\*(?!\*)(.*?)\*(?!\*)/g, "<em>$1</em>");

  // Underline: __text__
  markdown = markdown.replace(/__(.*?)__/g, "<u>$1</u>");

  // Strikethrough: ~~text~~
  markdown = markdown.replace(/~~(.*?)~~/g, "<s>$1</s>");

  // Images: ![alt](url "optional title")
  markdown = markdown.replace(/!\[(.*?)\]\((\S+?)(?:\s+"(.*?)")?\)/g, (_, alt, url, title) => {
    const titleAttr = title ? ` title="${title}"` : '';
    return `<a target="_blank" rel="noopener noreferrer" href="${url}"${titleAttr}><img src="${url}" alt="${alt}"${titleAttr} /></a>`;
  });

  // Links: [text](url)
  // Detect standalone links (only content on their line) and add a class
  markdown = markdown.replace(/^(\s*)\[(.*?)\]\((.*?)\)(\s*)$/gm, "<a href=\"$3\" rel=\"noopener noreferrer\" target=\"_blank\" class=\"standalone-link\">$2</a>");
  // Regular links (inline with other text)
  markdown = markdown.replace(/\[(.*?)\]\((.*?)\)/g, "<a href=\"$2\" rel=\"noopener noreferrer\" target=\"_blank\">$1</a>");

  // Auto-link plain URLs (fallback for URLs not wrapped in markdown)
  // Match http(s)://, www., or common TLDs, but avoid URLs already in <a> tags or code blocks
  markdown = markdown.replace(
    /(?<!<a [^>]*href="|<code>|href="|src=")((?:https?:\/\/|www\.)[^\s<>"]+)/gi,
    (match) => {
      // Ensure URL starts with protocol
      const url = match.startsWith('www.') ? `https://${match}` : match;
      // Remove trailing punctuation that's likely not part of the URL
      const cleanUrl = url.replace(/[.,;:!?)\]]+$/, '');
      const trailingPunct = url.slice(cleanUrl.length);
      return `<a href="${cleanUrl}" rel="noopener noreferrer" target="_blank">${cleanUrl}</a>${trailingPunct}`;
    }
  );

  // Format phone numbers globally (not just in tables)
  // Match +1XXXXXXXXXX format
  markdown = markdown.replace(/\+1(\d{10})/g, (match, digits) => {
    return formatUSPhoneNumberPretty(digits);
  });
  
  // Match plain 10-digit numbers that look like phone numbers (XXX-XXX-XXXX, XXX.XXX.XXXX, or XXXXXXXXXX)
  markdown = markdown.replace(/\b(\d{3})[-.]?(\d{3})[-.]?(\d{4})\b/g, (match, area, prefix, line) => {
    // Only format if not already formatted and not part of a larger number
    if (match.includes('(')) return match; // Already formatted
    return formatUSPhoneNumberPretty(area + prefix + line);
  });

  // Ordered lists - treat numbered items with just bold as headers, not lists
  markdown = markdown.replace(/^\d+\.\s+(<strong>.*?<\/strong>)\s*$/gm, '<p>$1</p>');

  // Unordered lists - handle both standard and indented items
  // Match groups of list items (both - and indented -)
  markdown = markdown.replace(/((?:^(?:\s{2,})?[-*+]\s+.*$\n?)+)/gm, (match) => {
    const lines = match.trim().split('\n');
    const items = lines.map(line => {
      // Remove leading whitespace and list marker
      const content = line.replace(/^\s*[-*+]\s+/, '').trim();
      return `<li>${content}</li>`;
    });
    return `<ul>${items.join('')}</ul>`;
  });

  // Edge case: catch any remaining table-like structures that weren't matched (e.g., tables within other content)
  // This is a fallback for tables that may have been missed by the primary regex
  markdown = markdown.replace(
    /(\|[^\n]+\|)\s*\n\s*(\|[-:| ]+\|)\s*\n((?:\s*\|[^\n]+\|\s*\n?)+)/g,
    (match, headerLine, separatorLine, bodyLines) => {
      // Skip if already processed (contains <table> tag)
      if (match.includes('<table>')) return match;
      
      const processCellContent = (str = '') => {
        let processed = str
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        processed = processed
          .replace(/&lt;(a)\s+([^&]*?)&gt;/gi, '<$1 $2>')
          .replace(/&lt;\/(a)&gt;/gi, '</$1>')
          .replace(/&lt;(\/?)(strong|em|u|s|code|mark|br|ul|ol|li|p|hr)(\s*\/?)&gt;/gi, '<$1$2$3>');
        return processed;
      };
      
      const headerCells = headerLine.trim().split('|').slice(1, -1);
      const alignments = separatorLine.trim().split('|').slice(1, -1).map((cell) => {
        const t = cell.trim();
        if (/^:-+:$/.test(t)) return 'center';
        if (/^:-+/.test(t)) return 'left';
        if (/-+:$/.test(t)) return 'right';
        return 'left';
      });
      const headers = headerCells
        .map((h, i) => `<th style="text-align:${alignments[i] || 'left'}">${processCellContent(h.trim())}</th>`)
        .join('');
      const body = (bodyLines || '').trim();
      const rowLines = body ? body.split('\n').filter(line => line.trim().startsWith('|')) : [];
      const rows = rowLines
        .map(row => {
          const cells = row.trim().split('|').slice(1, -1);
          return `<tr>${cells.map((c, i) => `<td style="text-align:${alignments[i] || 'left'}">${processCellContent(c.trim())}</td>`).join('')}</tr>`;
        })
        .join('');
      return `<div style="overflow-x: auto; max-width: 100%; -webkit-overflow-scrolling: touch;"><table><thead><tr>${headers}</tr></thead>${rows ? `<tbody>${rows}</tbody>` : ''}</table></div>`;
    }
  );

  // Paragraphs - improved to avoid wrapping already-tagged content and empty lines
  markdown = markdown.replace(/^(?!<\/?(h\d|ul|ol|li|blockquote|pre|table|thead|tbody|tr|td|th|hr|div)\b)(?!\s*$)(.+)$/gm, (match) => {
    const trimmed = match.trim();
    // Don't wrap if it's already an HTML tag, empty, or starts with a list marker
    if (trimmed.startsWith('<') || trimmed === '' || /^\s*[-*+]\s/.test(trimmed)) return match;
    return `<p>${trimmed}</p>`;
  });

  // Escape unsafe HTML tags (preserve known ones including div for table wrappers)
  markdown = markdown.replace(/<(?!\/?(a|img|code|pre|strong|em|u|s|blockquote|ul|ol|li|p|h\d|table|tr|td|th|tbody|thead|hr|mark|br|div)\b)[^>]*>/gi, match =>
    match
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
  );

  return convertTimestampsToReadable(markdown);
};

export const removeMarkdown = (input) => {
  if (input == null) return '';
  let text = String(input);
  // Strip code fences first to avoid inner pattern conflicts
  text = text.replace(/```[\s\S]*?```/g, '');
  // Strip inline code
  text = text.replace(/`([^`]+)`/g, '$1');
  // Headings
  text = text.replace(/^#+\s*(.*?)\s*#*$/gm, '$1');
  // Bold / italic / strikethrough
  text = text.replace(/(?:\*\*|__)(.*?)\1/g, '$1');
  text = text.replace(/(?:\*|_){1,3}([^*_]+?)(?:\*|_){1,3}/g, '$1');
  text = text.replace(/~~(.*?)~~/g, '$1');
  // Images (drop entirely)
  text = text.replace(/!\[[^\]]*\]\([^)]*\)/g, '');
  // Links -> keep link text
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');
  // Blockquotes
  text = text.replace(/^\s*>+\s?/gm, '');
  // Lists (unordered & ordered)
  text = text.replace(/^\s*[-*+]\s+/gm, '');
  text = text.replace(/^\s*\d+\.\s+/gm, '');
  // Tables: remove header + separator lines entirely; convert data rows to plain spaced text
  // Header line: | Col1 | Col2 | ...
  // Separator: | ---- | :---: | ---: |
  text = text.replace(/^(\|[^\n]+\|)\n(\|[ \-:|]+\|)$/gm, ''); // drop header + separator pair
  // Remaining table body rows: strip pipes but keep cell spacing
  text = text.replace(/^\|.*\|$/gm, line => line
    .split('|')
    .slice(1, -1)
    .map(c => c.trim())
    .filter(Boolean)
    .join(' ')
  );
  // HTML tags
  text = text.replace(/<[^>]+>/g, '');
  // Entities (basic)
  text = text.replace(/&nbsp;/g, ' ');
  // Collapse excessive newlines / spaces
  text = text.replace(/\r+/g, '')
             .replace(/\n{3,}/g, '\n\n')
             .replace(/[ \t]{2,}/g, ' ');
  return text.trim();
};

// Chat notification sound preference
const NOTIFICATION_SOUND_KEY = 'chat_notification_sound_enabled';

/**
 * Get the current notification sound preference
 * @returns {boolean} true if sound is enabled, false if disabled (defaults to true)
 */
export const isNotificationSoundEnabled = () => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return true;
  const stored = localStorage.getItem(NOTIFICATION_SOUND_KEY);
  return stored === null ? true : stored === 'true';
};

/**
 * Set the notification sound preference
 * @param {boolean} enabled - true to enable, false to disable
 */
export const setNotificationSoundEnabled = (enabled) => {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  localStorage.setItem(NOTIFICATION_SOUND_KEY, enabled ? 'true' : 'false');
};

/**
 * Toggle the notification sound preference
 * @returns {boolean} The new state (true if now enabled, false if now disabled)
 */
export const toggleNotificationSound = () => {
  const newState = !isNotificationSoundEnabled();
  setNotificationSoundEnabled(newState);
  return newState;
};

// Ensure a single global audio element exists and is attached to the DOM
/**
 * Ensure a single shared <audio> element exists for TTS / playback.
 * Safe for SSR (returns null) and idempotent across multiple callers.
 */
export const ensureGlobalAudio = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  // Prefer stable id, fall back to legacy selectors for backward compatibility
  let audio = document.getElementById('globalAudio_core') || document.getElementById('globalAudio') || document.querySelector('[id^="globalAudio_"]');
  if (!audio) {
    try {
      audio = document.createElement('audio');
      audio.id = 'globalAudio_core';
      audio.setAttribute('preload', 'auto');
      audio.setAttribute('playsinline', ''); // iOS inline playback
      audio.setAttribute('data-managed', 'true');
      // Do NOT set crossOrigin — blob: URLs are same-origin and crossOrigin='anonymous'
      // causes Chrome to reject them with NotSupportedError.
      (document.body || document.documentElement).appendChild(audio);
    } catch (e) {
      // In extremely locked-down environments just bail gracefully
      return null;
    }
  } else {
    // Normalize attributes if an older element exists
    if (!audio.getAttribute('playsinline')) audio.setAttribute('playsinline', '');
    if (!audio.getAttribute('preload')) audio.setAttribute('preload', 'auto');
    // Remove crossOrigin if it was set by an older version — causes blob: URL errors
    if (audio.hasAttribute('crossorigin')) audio.removeAttribute('crossorigin');
  }
  return audio;
};

export const streamAudio = (input, id, onComplete, onError) => {
  const abortController = new AbortController();
  const audio = ensureGlobalAudio();
  if (!audio) { onError?.(false); return () => {}; }

  const canStreamMP3 = typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg');

  // --- Fallback: collect-all-then-play (Safari / unsupported browsers) ---
  if (!canStreamMP3) {
    fetch(config.openai.TTS_URL, {
      method: 'POST',
      body: JSON.stringify({ input, id }),
      headers: { 'Content-Type': 'application/json' },
      signal: abortController.signal
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP error: ${r.status}`); return r.blob(); })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        audio.src = url;
        audio.play().catch(() => {});
        onComplete?.();
      })
      .catch(err => {
        if (err.name === 'AbortError') { onError?.(true); }
        else { onError?.(false); debug.error('Error streaming audio', err); }
      });
    return () => abortController.abort();
  }

  // --- Primary path: MediaSource streaming — plays as first bytes arrive ---
  // Pause any currently-playing audio so the src change below doesn't cause a brief artifact
  try { audio.pause(); } catch (_) {}

  const mediaSource = new MediaSource();
  const objectUrl = URL.createObjectURL(mediaSource);
  audio.src = objectUrl;

  const pendingChunks = [];
  let sourceBuffer = null;
  let fetchDone = false;
  let playing = false;
  let totalBytesReceived = 0;

  // 40 KB ≈ 2.5 seconds at 128 kbps — enough headroom to absorb network jitter
  // without noticeable start-of-stream stuttering.
  const MIN_BUFFER_BYTES = 40 * 1024;

  const tryAppend = () => {
    if (!sourceBuffer || sourceBuffer.updating || !pendingChunks.length) return;
    try { sourceBuffer.appendBuffer(pendingChunks.shift()); } catch (_) {}
  };

  const tryFinalize = () => {
    if (!fetchDone || (sourceBuffer && sourceBuffer.updating) || pendingChunks.length) return;
    try { mediaSource.endOfStream(); } catch (_) {}
    URL.revokeObjectURL(objectUrl);
    // Ensure playback starts if the entire clip was smaller than MIN_BUFFER_BYTES
    tryPlay();
    onComplete?.();
  };

  const tryPlay = () => {
    if (playing) return;
    // Hold off until MIN_BUFFER_BYTES have been received (or all data is in) to avoid
    // starting playback on a near-empty buffer, which causes the initial skip/glitch.
    if (!fetchDone && totalBytesReceived < MIN_BUFFER_BYTES) return;
    playing = true;
    audio.play().catch(() => {});
  };

  mediaSource.addEventListener('sourceopen', () => {
    try { sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg'); }
    catch (_) { return; }

    sourceBuffer.addEventListener('updateend', () => {
      tryPlay();
      if (pendingChunks.length) tryAppend();
      else tryFinalize();
    });

    tryAppend(); // flush any chunks that arrived before sourceopen
  });

  fetch(config.openai.TTS_URL, {
    method: 'POST',
    body: JSON.stringify({ input, id }),
    headers: { 'Content-Type': 'application/json' },
    signal: abortController.signal
  })
    .then(r => { if (!r.ok) throw new Error(`HTTP error: ${r.status}`); return r.body.getReader(); })
    .then(reader => {
      function read() {
        reader.read().then(({ done, value }) => {
          if (abortController.signal.aborted) { reader.cancel(); return; }
          if (done) { fetchDone = true; tryFinalize(); return; }
          totalBytesReceived += value.byteLength;
          pendingChunks.push(value);
          tryAppend();
          read();
        }).catch(() => {});
      }
      read();
    })
    .catch(err => {
      if (err.name === 'AbortError') { onError?.(true); }
      else { onError?.(false); debug.error('Error streaming audio', err); }
    });

  return () => {
    abortController.abort();
    try { mediaSource.endOfStream(); } catch (_) {}
  };
};

/**
 * Fetch TTS audio and resolve to a blob URL ready for use in an <audio> element.
 * Returns { promise, abort } so callers can cancel mid-fetch.
 * The caller is responsible for revoking the blob URL via URL.revokeObjectURL().
 */
export const fetchAudioBlob = (input, id, hasStoredAudio = false) => {
  const abortController = new AbortController();

  const promise = (async () => {
    const state = store.getState();
    const account_id = state.session?.account?.account_id;
    const cognito_id = state.authentication?.user?.cognito_id;
    const token = state.session?.token;
    const apiBase = config.apiGateway[0].endpoint;

    // Only hit the DB endpoint if query.raw is true (audio has been stored)
    if (hasStoredAudio && account_id && cognito_id) {
      try {
        const storedResponse = await fetch(
          `${apiBase}/prompts/get-query-audio/${id}/${account_id}/${cognito_id}`,
          { method: 'GET', headers: { Authorization: token }, signal: abortController.signal }
        );
        if (storedResponse.status === 200) {
          const blob = await storedResponse.blob();
          if (blob.size > 0) return URL.createObjectURL(blob);
        }
      } catch (err) {
        if (err.name === 'AbortError') throw err;
        // Unexpected error — fall through to generate
      }
    }

    // Generate via TTS — backend saves it to queries.raw
    const ttsResponse = await fetch(config.openai.TTS_URL, {
      method: 'POST',
      body: JSON.stringify({ input, id }),
      headers: { 'Content-Type': 'application/json' },
      signal: abortController.signal
    });
    if (!ttsResponse.ok) throw new Error(`TTS error: ${ttsResponse.status}`);
    const blob = await ttsResponse.blob();
    if (!blob.size) throw new Error('TTS returned empty audio');
    return URL.createObjectURL(blob);
  })();

  return { promise, abort: () => abortController.abort() };
};

export const checkMicPermissions = async () => {
  return navigator.permissions.query({ name: "microphone" })
    .then(permissionStatus => {
      return permissionStatus.state;
    });
};

export const getMergeMessage = (message = "", user) => {
  if (!message || !user) return "";
  return message?.replace(/{{([^}]+)}}/g, function (match, string) {
    var fallback = "";
    var stringArr = string.split("|");
    var value = stringArr[0].trim();

    if (stringArr[1] && string.includes("fallback")) {
      const matches = stringArr[1].match(/(?!fallback:\s?)(["'].*?["'])/);
      if (matches && matches.length) fallback = matches[0].replace(/["']/g, "").trim();
    }
    return user[value] ? user[value] : fallback;
  });
};

export const generateMnemonic = (number = 2) => {
  try {
    const username = generateSlug(number, {
      format: "kebab",
      partsOfSpeech: ["adjective", "noun"],
      categories: {
        adjective: ["appearance", "size", "touch"],
        noun: ["animals", "science", "technology", "place"],
      },
    });
    return `${username}${uniqueID("-", 6, "numbers")}`;
  } catch (error) {
    debug.error('Error generating mnemonic', error);
    return null;
  }
};

export const getScaledPercentage = (percentage) => {
  const minPercentage = 1; // Threshold where scaling kicks in
  const scaleFactor = 2; // Adjust to control exaggeration of small values

  if (percentage < minPercentage) {
    return percentage * scaleFactor; // Exaggerate small values
  } else if (percentage < 5) {
    return percentage + (scaleFactor - 1); // Slightly boost medium-small values
  }
  return percentage; // Keep large values as is
};

export const runTranslateFix = () => {
  if (typeof Node === 'function' && Node.prototype) {
    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function (child) {
      if (child.parentNode !== this) {
        if (console) {
          debug.error('Cannot remove a child from a different parent', { child, parent: this });
        }
        return child;
      }
      return originalRemoveChild.apply(this, arguments);
    }

    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function (newNode, referenceNode) {
      if (referenceNode && referenceNode.parentNode !== this) {
        if (console) {
          debug.error('Cannot insert before a reference node from a different parent', { referenceNode, parent: this });
        }
        return newNode;
      }
      return originalInsertBefore.apply(this, arguments);
    }
  }
};

// Adjusts the alpha channel of an existing rgba()/rgb()/hex color string.
// Usage: rgbaOpacity('rgba(255, 255, 255, 0.3)', 0.6) => 'rgba(255,255,255,0.6)'
//        rgbaOpacity('rgb(34, 50, 100)', 0.2) => 'rgba(34,50,100,0.2)'
//        rgbaOpacity('#ffffff', 0.4) => 'rgba(255,255,255,0.4)'
// Returns original string on parse failure.
export const rgbaOpacity = (color, alpha) => {
  try {
    if (typeof color !== 'string') return color;
    const a = Math.min(1, Math.max(0, Number(alpha)));
    // Hex (#rgb or #rrggbb)
    if (/^#([0-9a-f]{3}){1,2}$/i.test(color)) {
      let hex = color.substring(1);
      if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
      const num = parseInt(hex, 16);
      const r = (num >> 16) & 255;
      const g = (num >> 8) & 255;
      const b = num & 255;
      return `rgba(${r},${g},${b},${a})`;
    }
    // rgba or rgb
    const rgbaMatch = color.replace(/\s+/g,'').match(/^rgba?\((\d{1,3}),(\d{1,3}),(\d{1,3})(?:,(0|0?\.\d+|1))?\)$/i);
    if (rgbaMatch) {
      const r = Math.min(255, parseInt(rgbaMatch[1],10));
      const g = Math.min(255, parseInt(rgbaMatch[2],10));
      const b = Math.min(255, parseInt(rgbaMatch[3],10));
      return `rgba(${r},${g},${b},${a})`;
    }
    return color; // fallback if pattern not recognized
  } catch(_) {
    return color;
  }
};